/**
 * Profile.tsx — the /me surface. Shows the signed-in person their own profile: the identity the
 * backend cached from Microsoft Graph, their platform record, what they've declared about
 * themselves, and the tools they've shared and adopted.
 *
 * OWNERSHIP (db_auth_flow_spec Part 1): this page talks ONLY to our backend. It calls GET /api/me,
 * GET /api/me/tools, GET /api/me/adopted and GET /api/me/support-requests (same-origin via the Vite dev
 * proxy) using the app session cookie — it never calls Microsoft, never holds a token, and never sees
 * the Entra oid. Every one of those is under /api/me, which is the only route family that returns
 * private content, and none of them takes a user id: the server derives the owner from the session, so
 * there is no id on the wire from here to get wrong.
 *
 * PRIVACY (Stage 5): the shared-tools and adopted-tools lists are the OWNER's own view. They are
 * fetched only from /api/me/* (server-scoped to the session user); there is no route that returns
 * another person's private lists, and this page is only ever the viewer's own profile this stage.
 * The private sections are visibly marked "only you can see this" (PR-F06).
 *
 * SELF-DECLARED EDIT (Stage 5.2, bio-only slice): the owner can edit their own bio in place via
 * PUT /api/me/bio, then the page revalidates the /me loader to re-read the saved value. Every other
 * self-declared field (interests/skills/competency/maker/certifications) remains display-only this
 * slice — those edit paths are later steps. Self-declared items carry the mandatory
 * "self-declared — not verified by the platform" label (PR-F49 / UR-NFR06).
 *
 * GRACEFUL DEGRADATION (PC-F09 / UR-F04): a directory field Graph did not return is HIDDEN — no row,
 * no placeholder. Blank is an accurate answer, and §15 says the non-production accounts carry sparse
 * directory data, so a note per missing field was the common case rather than the edge one. The row
 * helper that decides this is `Field` in ./people/fields, shared with both public-profile pages so
 * the three surfaces cannot answer the question differently. A section with no data (no bio, no
 * shared tools) is omitted entirely — an empty profile is a normal profile, with no completion meter
 * or prompt (PR-NFR09), and with every row now hideable that omission has to be checked at the
 * SECTION level too or a heading is left standing over an empty list.
 *
 * ACCESSIBILITY (PC-NFR01): labelled <section> regions, semantic <dl> for field pairs, alt text on
 * the avatar, AA-contrast tokens.
 */
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Link, useLoaderData, useNavigate, useRevalidator } from 'react-router';
import {
  isExplained,
  MAX_EXPLANATION_LENGTH,
  numberComponents,
  type FlowComponent,
  type FlowComponentDoc,
} from '@trailblazer/shared';
import { monogram, resolveAvatar, STOCK_AVATARS } from './avatar';
import { newBadgeKeys, markBadgesSeen } from './seenBadges';
import { deleteFollow, type FollowingEntry } from './community/api';
import { getMySupportRequests, type MySupportRequest } from './community/supportApi';
import { movementText, type Movement } from './leaderboard/api';
import AccordionGallery, { type AccordionGalleryItem } from './tools/AccordionGallery';
import { Button } from './tools/upload/design/Button';
import { BentoGrid, BentoGridItem } from './tools/upload/design/BentoGrid';
import { PadlockIcon, LibraryIcon } from './tools/upload/design/uploadIcons';
/**
 * The decorative WebGL band backdrop, CODE-SPLIT out of the main bundle.
 *
 * It pulls in `ogl` (a WebGL library), which is most of a 250kB-gzipped main chunk that every route
 * was paying for — including the routes with no backdrop at all. `lazy` moves `ogl` into its own chunk
 * fetched only when a profile page actually mounts.
 *
 * Safe to defer because the backdrop is PURELY decorative and takes no layout space: it is
 * `position: absolute; inset: 0; pointer-events: none` inside the band (see .pp-band__topo), so the
 * `null` Suspense fallback below renders nothing, shifts nothing, and blocks no interaction. The band
 * keeps its own `min-height` and background either way — the contours just fade in a beat later.
 */
const Topography = lazy(() => import('./design/Topography'));

/**
 * The generated per-tool preview art. STATIC, unlike Topography above, and that is measured rather than
 * casual: `lazy(() => import('./design/FlowArt'))` here is a verified no-op.
 *
 * `design/FlowArt` is STATICALLY imported by `tools/upload/design/PreviewCard.tsx` and
 * `ReviewPreviewCard.tsx`, and the upload overlay itself is a static import at
 * `tools/ShareOverlay.tsx` — nothing in this app splits it. So FlowArt and its geometry module are in
 * the main chunk whatever this line says, and Rollup says so out loud: a `lazy` here earns the
 * "dynamically imported by … but also statically imported by … dynamic import will not move module into
 * another chunk" warning, plus one <Suspense> boundary per tile that can never suspend. A comment
 * claiming a code-split that does not happen is worse than no comment.
 *
 * TO ACTUALLY SPLIT IT, the upload overlay has to be split first; only then does deferring the art on
 * this page buy a visitor anything. At that point make this `lazy` with a `null` fallback per tile — the
 * boxes have fixed heights (`h-tool-card-thumb`, and the accordion panel's own 270px), so nothing would
 * shift — and put the <Suspense> on each tile rather than around the grid, so a slow chunk could not
 * blank a whole row of names and status lines.
 */
import { FlowArt } from './design/FlowArt';
import { staggerContainer } from './tools/upload/design/motion';
import { KombiJourney } from './design/KombiJourney';
import { BadgeIcon } from './design/BadgeIcon';
import './profilePage.css';
// UR-F04: the ONE row helper. This page used to declare its own, which rendered a visible
// "Not provided by the directory" for every empty value — the exact behaviour the requirement says to
// replace with nothing at all. Importing the shared one instead of re-fixing it here is what stops the
// three profile surfaces drifting apart again; see the header of ./people/fields.
import { Field } from './people/fields';
// Re-exported so importers that predate the extraction to ./avatar (App.tsx, ToolDetail.tsx,
// people/PersonProfile.tsx — all from the main merge) keep resolving these from ./Profile. The
// canonical home is ./avatar; this only preserves the older import path. Future cleanup: point
// those call sites at ./avatar directly and drop this re-export.
export { monogram, STOCK_AVATARS } from './avatar';

/** A safe tool summary as /api/me/tools and /api/me/adopted return it (backend db/profile.ts). */
export interface ProfileToolSummary {
  toolId: string;
  name: string;
  /** Present on the owner's own submission rows; absent from saved/adopted summaries. */
  description?: string;
  categoryLabel?: string | null;
  /** Builder department snapshot, not the tool's applies-to department. */
  builderDepartment?: string | null;
  /** Set only when the tool has an approved review. */
  verified?: boolean;
  publicationStatus: string;
  healthState: string;
  lastUpdated: string | null;
  /**
   * True when the owner has withdrawn this tool from the catalogue. Tracked separately from
   * publicationStatus (the tool keeps its status, it is just hidden), so the list can label it.
   * Only the owner's own submission list carries a true value; adopted/saved lists leave it false.
   */
  withdrawn: boolean;
  /**
   * RT-F20 — the builder's OWN tool's current position + movement in the weekly Top 5, present only on
   * the owner's shared-tools list (GET /api/me/tools) and only when the tool is in the live Top 5.
   * Null/absent otherwise (the adopted/saved lists never carry it). AGGREGATE only — ranks the tool,
   * never a person (RT-F22 / RT-NFR05).
   */
  leaderboard?: { position: number; movement: Movement; movementPlaces: number } | null;
  /**
   * PR-F38 — the four activity figures behind the builder's recognition view. The server populates this
   * on the owner's OWN submission list (mySubmissions) and leaves it off the adopted/saved lists, where
   * the figures belong to somebody else's tool — hence optional here, matching `activity?` on the
   * server's own `ProfileToolSummary`. It is NEVER `undefined` on a row that has one: a tool with no
   * activity yet carries real zeros, because these are counts of the builder's own tool's events and
   * "nobody has adopted it yet" is a true, disclosable fact rather than a suppressed one.
   */
  activity?: ToolActivityFigures | null;
  /**
   * The tool's flow TOPOLOGY, encoded (backend `ProfileToolSummary.flowShape`), for the generated
   * preview art. REQUIRED-and-nullable, unlike `leaderboard`/`activity` above: those two are absent on
   * the lists where the figures belong to somebody else, whereas every list here — submissions,
   * adopted and saved — sends this one, and null already carries the only other meaning it has, "we
   * could not read the flow". Making it optional would add a second way to say the same thing and let
   * a list that forgot it look like a tool with no readable definition.
   */
  flowShape: string | null;
}

/**
 * PR-F38's four figures for one of the builder's own tools (backend db/profile.ts ToolActivityFigures).
 * HAND-WRITTEN MIRROR — nothing generates it, so a server-side change compiles clean on both sides and
 * silently drops a field. Proved against the wire, not the type: on the seed, Ada Builder's Expense
 * Report Autofiller arrives as `{adoptions:3, likes:3, comments:0, openIssues:0}`.
 *
 * COUNTS ONLY, NEVER IDENTITIES (PR-F38's own closing clause, and PR-F28): the server sends no user id
 * with any of these and there is deliberately no `saves` figure — a save is private to the saver
 * (FB-F02) and is not one of PR-F38's names.
 */
export interface ToolActivityFigures {
  /** Distinct colleagues who adopted it, the builder's own adoption excluded (AE-F10). */
  adoptions: number;
  likes: number;
  /** Every level, removed comments excluded (FB-F09) — conversation, not a page count. */
  comments: number;
  /** Unresolved fault reports — the count only, never a body or a reporter (FB-F06/TD-F09). */
  openIssues: number;
}

/**
 * One of the owner's earned badges as GET /api/me/badges returns it (backend db/profile.ts MyBadge).
 * Identity fields only — no count/total, no earned-at, no awarded-by (anti-ranking + privacy).
 */
export interface MyBadge {
  badgeKey: string;
  name: string;
  group: string;
  criterionText: string;
  hidden: boolean; // the owner opted this earned badge OUT of public display (0032); false = shown
  progressCurrent: number | null;
  progressTarget: number | null;
}

/**
 * One masterlist badge as GET /api/me/badges/panel returns it (backend db/profile.ts PanelBadge) —
 * a MyBadge plus an `earned` flag, because the full panel (PR-F44) lists EVERY badge: earned ones
 * (each with a shown/hidden switch) and not-yet-earned ones (rendered muted, with criterionText as the
 * how-to-earn text, PR-F45). Owner-private; no count/total (anti-ranking + privacy).
 */
export interface PanelBadge {
  badgeKey: string;
  name: string;
  group: string;
  criterionText: string;
  /** A private owner-panel-only key joining milestones on the same activity track. */
  progressGroup: string | null;
  earned: boolean;
  hidden: boolean; // the owner opted this earned badge OUT of public display (0032); false = shown
  progressCurrent: number | null;
  progressTarget: number | null;
}

/**
 * The one-line gloss shown under each badge-group heading in the panel — the requirements' OWN wording
 * for each §5.2 section (REQUIREMENTS_PROFILE §5.2). Nothing invented. A group with no gloss here (e.g.
 * a future Challenge group) simply shows its heading alone.
 */
const BADGE_GROUP_GLOSS: Record<string, string> = {
  Contributing: 'what you have shared',
  Reusing: 'what you have taken up',
  Helping: 'what you have given back',
  Learning: 'what you have built up',
  Recognition: 'awarded by a person, not by a rule',
};

/** The self-declared block as /api/me returns it (backend db/profile.ts SelfDeclared). */
export interface SelfDeclared {
  bio: string | null;
  interests: string[];
  skillsHave: string[];
  skillsWanted: string[];
  competencyLevel: string | null;
  makerType: string | null;
  avatarKind: string | null;
  avatarBlobRef: string | null;
  selfSectionHidden: boolean;
  /** The owner's opt-out from showing their chosen badges publicly (0030). Default false = shown. */
  badgesHidden: boolean;
}

/** The exact shape GET /api/me returns (backend db/identity.ts UserWithProfile). */
export interface MeResponse {
  user_id: string;
  display_name: string;
  upn: string;
  email: string | null;
  profile: {
    job_title: string | null;
    department: string | null;
    office_location: string | null;
    photo_blob_ref: string | null;
    manager_name: string | null;
    graph_synced_at: string | null;
  } | null;
  platform: {
    member_since: string;
    last_seen: string;
    status: string;
  };
  selfDeclared: SelfDeclared | null;
  certifications: { ordinal: number; name: string; year: number }[];
  /**
   * The first-run guided tour's state (0048, docs/GUIDED_TOUR.md). Read by the frame, not this page:
   * both null is the only state that shows the tour. Mirrored here so this interface stays a faithful
   * copy of the server's /api/me shape.
   */
  tour: {
    completed_at: string | null;
    skipped_at: string | null;
  };
}

/**
 * What the /me loader hands this component: either the profile plus the owner's tool lists, or a
 * signed-out marker. The tool lists are the owner's private view (see the loader in router.tsx).
 */
export type MeLoaderData =
  | {
      signedIn: true;
      me: MeResponse;
      shared: ProfileToolSummary[];
      adopted: ProfileToolSummary[];
      saved: ProfileToolSummary[];
      badges: MyBadge[];
      /** The owner's own follower COUNT (PR-F52 — public number, shown here read-only). */
      followerCount: number;
      /** The owner's own "who I follow" list (PR-F61) — visible to this person alone. */
      following: FollowingEntry[];
    }
  | { signedIn: false };

/** The max bio length — mirrors the server cap (routes/me.ts bioBody) and the DB CHECK (0002). */
const BIO_MAX = 280;

/**
 * NOTE: the FEATURED "View more" split (how many badges show before the rest are revealed inline)
 * lives ONLY on other people's views of a profile — see FEATURED_BADGES in UserProfile.tsx and
 * people/PersonProfile.tsx. This page is the OWNER's own profile, where the strip always shows every
 * non-hidden badge (no "View more" on your own page), so there is no featured constant here. There is
 * NO cap on how many badges a person can have or show: every earned badge is public by default.
 */

/**
 * Custom-avatar upload caps (Stage 5.2 avatar slice 2). Kept in step with the server
 * (routes/me.ts AVATAR_MAX_BYTES) and the DB CHECK (migration 0020):
 *   - AVATAR_ORIGINAL_MAX_BYTES — the largest ORIGINAL file we let a person pick for cropping. The
 *     original is only decoded in the browser for the cropper; it is never uploaded.
 *   - AVATAR_OUTPUT_PX — the cropped output is re-encoded to a square of this side (256), matching
 *     the on-screen avatar's needs at 2x.
 *   - AVATAR_OUTPUT_MAX_BYTES — the ceiling on the re-encoded output we will upload (~200 KB
 *     decoded). We step the WebP quality down until the encode fits, so this is the same gate the
 *     server enforces on the decoded bytes.
 *   - AVATAR_ACCEPT — the file-picker MIME allowlist; matched again server-side after re-encode.
 */
const AVATAR_ORIGINAL_MAX_BYTES = 5 * 1024 * 1024; // 5 MB source cap (never uploaded)
const AVATAR_OUTPUT_PX = 256;
const AVATAR_OUTPUT_MAX_BYTES = 200 * 1024; // ~200 KB re-encoded output
const AVATAR_ACCEPT = 'image/png,image/jpeg,image/webp';

/**
 * PUT the owner's bio to OUR backend (same-origin, session cookie). No user id is ever sent — the
 * backend derives the owner from the session. Returns the outcome so the caller can branch: 'ok' on
 * 204, 'invalid' on a 400 (with the field paths), 'error' otherwise. The browser never talks to
 * Microsoft here.
 */
async function putBio(
  bio: string,
): Promise<{ ok: true } | { ok: false; reason: 'invalid' | 'error'; detail?: unknown }> {
  const res = await fetch('/api/me/bio', {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ bio }),
  });
  if (res.ok) return { ok: true };
  if (res.status === 400) {
    const body = (await res.json().catch(() => null)) as { detail?: unknown } | null;
    return { ok: false, reason: 'invalid', detail: body?.detail };
  }
  return { ok: false, reason: 'error' };
}

/**
 * PUT the owner's avatar choice to OUR backend (same-origin, session cookie). No user id is sent —
 * the backend derives the owner from the session, and validates the stock id against its own
 * allowlist. `kind:'directory'` reverts to the directory photo. Returns the outcome like putBio.
 */
async function putAvatar(
  body:
    | { kind: 'stock'; stockId: string }
    | { kind: 'upload'; dataUri: string }
    | { kind: 'directory' },
): Promise<{ ok: true } | { ok: false; reason: 'invalid' | 'error'; detail?: unknown }> {
  const res = await fetch('/api/me/avatar', {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  if (res.ok) return { ok: true };
  if (res.status === 400) {
    const errBody = (await res.json().catch(() => null)) as { detail?: unknown } | null;
    return { ok: false, reason: 'invalid', detail: errBody?.detail };
  }
  return { ok: false, reason: 'error' };
}

/**
 * SET whether one of the owner's earned badges is HIDDEN from their public profile (PR-F43, per-badge
 * opt-out, 0032). Same-origin, session cookie; no user id is ever sent — the backend derives the owner
 * from the session and the badge key names the badge, not the person. The body carries the DESIRED
 * state `{ hidden }`, so the call is idempotent (no toggle race) — the panel sends what its switch
 * shows. Outcomes:
 *   - 204            → { ok: true }.
 *   - 409 not_earned → the badge is not earned (a server backstop — the panel only lists earned ones).
 *   - anything else  → { ok: false, reason: 'error' }.
 */
async function putBadgeHidden(
  badgeKey: string,
  hidden: boolean,
): Promise<{ ok: true } | { ok: false; reason: 'not_earned' | 'error' }> {
  const res = await fetch(`/api/me/badges/${encodeURIComponent(badgeKey)}/hidden`, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ hidden }),
  });
  if (res.ok) return { ok: true };
  if (res.status === 409) return { ok: false, reason: 'not_earned' };
  return { ok: false, reason: 'error' };
}

/**
 * SET the owner's GLOBAL opt-out from showing ANY badges publicly (0030, PR-F43). Same-origin,
 * session cookie; no user id is sent — the backend derives the owner from the session. `hidden: true`
 * hides the whole badge set from OTHER viewers (the owner still sees their own strip); `false` shows
 * it. This is the coarse control layered on top of the per-badge hides and NEVER changes them — only
 * the whole set's public visibility. Returns 'ok' on 204.
 */
async function putBadgesPublic(
  hidden: boolean,
): Promise<{ ok: true } | { ok: false; reason: 'error' }> {
  const res = await fetch('/api/me/badges/public', {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ hidden }),
  });
  return res.ok ? { ok: true } : { ok: false, reason: 'error' };
}

/**
 * Fetch the owner's FULL badge panel (Stage 5.3 Slice 3, PR-F44) from OUR backend (same-origin,
 * session cookie). Returns EVERY masterlist badge with this person's earned/hidden state so the
 * panel can render earned badges as switches and not-yet-earned ones muted with their how-to-earn text.
 * No user id is ever sent — the backend derives the owner from the session. Fetched lazily when the
 * panel opens (not in the /me loader), so the profile-load path is unchanged. Returns [] on any error
 * (the panel then shows nothing rather than throwing); the browser never talks to Microsoft here.
 */
async function fetchBadgePanel(): Promise<PanelBadge[]> {
  try {
    const res = await fetch('/api/me/badges/panel', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) return [];
    const body = (await res.json()) as { badges?: PanelBadge[] };
    return body.badges ?? [];
  } catch {
    return [];
  }
}

// ── The contribution summary (UR-F21 / PR-F27 / PR-F56 / UR-F33) ──────────────────────────────────
// A CLOSED set of six figures, three shown by default, and the person chooses which appear on their
// public profile. The chooser is here; the read-only rendering a colleague sees is in UserProfile.tsx
// and people/PersonProfile.tsx, which share the label map below so the six figures are never named
// two different ways on two different screens.

/**
 * One figure as the OWNER's own chooser needs it, mirroring backend `db/profile.ts`
 * `MyContributionFigure` (`GET /api/me/contribution-display`).
 *
 * HAND-WRITTEN MIRROR — nothing generates this file, so a server rename compiles clean here and the
 * chooser silently renders nothing. Proved against the wire, not the type: on the seed this endpoint
 * returns `{"figure":"total_adoptions","shown":true,"value":12,"withheldByBadgePrivacy":false}`.
 *
 * `figure` is a plain string on purpose. The server owns the closed set (0012's CHECK constraint) and
 * re-declaring the union here would let a client-side list of six drift from it; instead the renderer
 * looks the name up in `CONTRIBUTION_FIGURE_LABEL` and SKIPS anything it does not recognise, so a
 * seventh figure shipped server-first cannot render as a raw column name.
 *
 * `value` is present even for a figure the person has switched OFF — correct here, and the reason this
 * is a different endpoint and a different type from the public one, which carries no unchosen figure
 * at all. Choosing sensibly needs the number; publishing it must not.
 */
interface MyContributionFigure {
  figure: string;
  shown: boolean;
  value: number;
  /** `shown` but `badges_earned` is STILL withheld publicly, because the person has also hidden a badge —
   *  a count next to a shortened badge list reconstructs the opt-out by subtraction. Surfaced so the row
   *  can say so: a switch that silently does nothing is worse than one that explains itself. Only ever
   *  true for that figure. (It used to be the ONLY such flag; `PR-F30` added the second, below.) */
  withheldByBadgePrivacy: boolean;
  /** `shown` but `learning_completed` is STILL withheld publicly, because the person has hidden a training
   *  step they completed — "Completed" means all seven, so publishing it would say the hidden step was
   *  done after all (`PR-F30`; server rule 3 in `readPublicContributionFiguresWith`). A SEPARATE flag from
   *  the badge one because the row has to name the remedy, and "unhide a step" is not "unhide a badge".
   *  Only ever true for that figure. */
  withheldByLearningPrivacy: boolean;
}

/**
 * figure name → the words `UR-F21` uses for it, verbatim ("tools published", "total adoptions of their
 * tools", "comments made", "forum posts and replies", "learning completed", "badges earned"), adapted
 * to second person for the owner's own chooser and used as-is on a colleague's profile.
 *
 * THE ONE PLACE THE SIX ARE NAMED IN THE CLIENT, exported for `UserProfile.tsx` and
 * `people/PersonProfile.tsx` — three surfaces render this summary and a label duplicated per surface
 * is how the same figure ends up called two things. It is also the client's recognition test: a figure
 * name absent from this map is skipped rather than rendered, so a server-side addition degrades to
 * "not shown yet" instead of leaking a column name onto a public page.
 *
 * NOT an ordering and NOT a whitelist of what may be published — the server decides both. Labels only.
 */
export const CONTRIBUTION_FIGURE_LABEL: Record<string, string> = {
  tools_published: 'Tools published',
  total_adoptions: 'Total adoptions of their tools',
  comments_made: 'Comments made',
  forum_posts: 'Forum posts and replies',
  learning_completed: 'Learning completed',
  badges_earned: 'Badges earned',
};

/**
 * How one figure's value reads to a person.
 *
 * `learning_completed` is a 0/1 STATE on the server (all seven training steps and a passed knowledge
 * check — the same measure the `safe_hands` badge and the learning dashboard use), so it must never be
 * printed as "1": a profile saying "Learning completed: 1" is a number that means nothing. Every other
 * figure is a genuine count and is printed as itself, INCLUDING zero — a real zero is an answer, and
 * hiding it would be the "fabricated absence" mistake in reverse.
 */
export function contributionFigureValueText(figure: string, value: number): string {
  if (figure === 'learning_completed') return value > 0 ? 'Completed' : 'Not yet completed';
  return String(value);
}

/**
 * The owner's own chooser data: all six figures, their current choice and their real values.
 * Same-origin, session cookie, no id sent — the backend derives the owner from the session.
 *
 * Returns null on ANY failure, which the section reports in words. It must not collapse to `[]`: an
 * empty list and a failed read look identical on screen, and only one of them means "you have chosen
 * to publish nothing".
 */
async function fetchContributionDisplay(): Promise<MyContributionFigure[] | null> {
  try {
    const res = await fetch('/api/me/contribution-display', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { figures?: MyContributionFigure[] };
    return body.figures ?? null;
  } catch {
    return null;
  }
}

/**
 * SET whether ONE figure appears on the owner's public profile (`UR-F33`). Same-origin, session
 * cookie; NO user id is sent anywhere — `:figure` names a figure, not a person, and the backend takes
 * the subject from the session, so this call has no shape in which it could target somebody else.
 *
 * The body carries the DESIRED state, so it is idempotent and two fast clicks cannot race into
 * opposite outcomes — the same shape the badge switches use. 204 on success; a 400 means the figure
 * name was not one of the closed six, which is a client bug, not something to show the person.
 */
async function putContributionDisplay(figure: string, shown: boolean): Promise<boolean> {
  try {
    const res = await fetch(`/api/me/contribution-display/${encodeURIComponent(figure)}`, {
      method: 'PUT',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ shown }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

// ── The chosen learning (PR-F30 / UR-F33) ─────────────────────────────────────────────────────────
// A CLOSED set of seven training steps, NONE shown by default, and the person chooses which of the ones
// they have completed appear on their public profile. The chooser is here, beside the contribution one it
// is modelled on; the read-only rendering a colleague sees is in UserProfile.tsx and
// people/PersonProfile.tsx, which share the label map below so a step is never named two different ways.

/**
 * One training step as the OWNER's own chooser needs it, mirroring backend `db/profile.ts`
 * `MyLearningStep` (`GET /api/me/learning-display`).
 *
 * HAND-WRITTEN MIRROR — nothing generates this file, so a server rename compiles clean here and the
 * chooser silently renders nothing. Proved against the wire, not the type: on the seed this endpoint
 * returns `{"stepNo":1,"completed":true,"shown":false}` for each of the seven.
 *
 * `completed` is present for steps the person has not chosen to publish, which is correct here and would
 * be a leak on the public shape: this is their own training record, and deciding what to publish needs to
 * know which steps there is anything to publish about. The public payload carries neither the unchosen
 * steps nor any `shown`/`completed` flag — see `PublicLearningStep` on the two profile pages.
 */
interface MyLearningStep {
  stepNo: number;
  completed: boolean;
  shown: boolean;
}

/**
 * step number → what to call that step on screen.
 *
 * THE CLIENT NAMES THE STEPS, AND THAT IS A PRIVACY DECISION, NOT LAZINESS. The server's own step titles
 * (`learn/trainingContent.ts`) ADAPT BY LEVEL for steps 1–3: a Beginner's step 1 is "What 'AI at work'
 * actually means", a Practitioner's is "AI at work — the essentials". Publishing the server's title on a
 * public profile would therefore disclose, to anyone who compared two profiles, the person's SELF-DECLARED
 * COMPETENCY LEVEL — which sits inside the block `UR-F18`/`PR-F12` let them hide entirely. So these labels
 * describe each step's SUBJECT, identically for everyone, and steps 4–7 use the wording of the training's
 * own level-independent titles. If the training's step subjects ever change, change these with them.
 *
 * THE ONE PLACE THE SEVEN ARE NAMED IN THE CLIENT, exported for `UserProfile.tsx` and
 * `people/PersonProfile.tsx` — three surfaces render this list and a label duplicated per surface is how
 * the same step ends up called two things. It is also the client's recognition test: a step number absent
 * from this map is skipped rather than rendered, so an eighth step shipped server-first degrades to "not
 * shown yet" instead of printing a bare number nobody can read.
 *
 * NOT an ordering and NOT a whitelist of what may be published — the server decides both. Labels only.
 */
export const LEARNING_STEP_LABEL: Record<number, string> = {
  1: 'Foundations — what AI at work means',
  2: 'Foundations — what these tools can and cannot do',
  3: 'Foundations — getting started safely',
  4: 'The rules — data and privacy',
  5: 'The rules — safety and ethics',
  6: 'Governance — review, adoption and health',
  7: 'Where to get help, and the knowledge check',
};

/**
 * The owner's own chooser data: all seven steps, whether they have completed each and whether they
 * publish it. Same-origin, session cookie, no id sent — the backend derives the owner from the session.
 *
 * Returns null on ANY failure, which the section reports in words. It must not collapse to `[]`: an empty
 * list and a failed read look identical on screen, and neither means what the other does.
 */
async function fetchLearningDisplay(): Promise<MyLearningStep[] | null> {
  try {
    const res = await fetch('/api/me/learning-display', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { steps?: MyLearningStep[] };
    return body.steps ?? null;
  } catch {
    return null;
  }
}

/**
 * SET whether ONE completed step appears on the owner's public profile (`PR-F30`/`UR-F33`). Same-origin,
 * session cookie; NO user id is sent anywhere — `:stepNo` names a step, not a person, and the backend
 * takes the subject from the session, so this call has no shape in which it could target somebody else.
 *
 * The body carries the DESIRED state, so it is idempotent and two fast clicks cannot race into opposite
 * outcomes. 204 on success; a 400 means the step number was outside the closed seven, which is a client
 * bug, not something to show the person.
 */
async function putLearningDisplay(stepNo: number, shown: boolean): Promise<boolean> {
  try {
    const res = await fetch(`/api/me/learning-display/${encodeURIComponent(String(stepNo))}`, {
      method: 'PUT',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ shown }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

// ── Owner tool actions (Will's ProfileFlow "clicks on a submitted tool" overlay) ──────────────────
// Same-origin, session-cookie helpers over /api/me/tools/:toolId — the actor is ALWAYS the session
// (no user id / token ever sent), matching putAvatar/putBio above. A tool that is not the caller's own
// returns 404. The backend owns the re-review rule; these helpers just carry its outcome back.

/** The owner's editable detail for one submitted tool (backend db/profile.ts MySubmissionDetail). */
export interface MySubmissionDetail {
  toolId: string;
  name: string;
  description: string;
  problemSolved: string;
  setupInstructions: string;
  dependencies: string;
  connectorSetupNotes: string | null;
  publicationStatus: string;
  healthState: string;
  version: number;
  withdrawn: boolean;
  lastUpdated: string | null;
  /** Reviewer's reason — present only for a Rejected tool (the "fix & resubmit" reason). */
  rejectionReason: string | null;
  rejectedAt: string | null;
  /**
   * The structured, per-step setup docs (backend 0020), in order — one card per flow component. Null
   * for a legacy/manually-entered tool, in which case the overlay falls back to a single setup-
   * instructions textarea. The step scaffolding (label/connector/kind) is display-only; the owner
   * edits only each step's `explanation`, exactly as the upload flow does.
   */
  steps: FlowComponentDoc[] | null;
  /**
   * True once the tool ever reached Approved (`date_published` set). The durable "was on the AI SWAT
   * track" signal: a step/connector edit re-queues for review only when the tool required SWAT in the
   * first place — a pure self-vouched Draft edits freely and stays Draft. The server is authoritative;
   * this only drives the pre-save warning + button copy.
   */
  wasApproved: boolean;
  /**
   * PR-F38 — the same four figures the submission list carries. REQUIRED here, not optional: the server
   * always sends an object on this route (an all-zero one for a tool with no activity yet), and
   * declaring it optional would let the overlay quietly render nothing on a shape that in fact arrived.
   * The list row and this overlay render it through the SAME component (`ToolActivity`) so they cannot
   * come to disagree about what a figure means.
   */
  activity: ToolActivityFigures;
}

/** GET the owner's editable detail for one of their tools. null on 404/error (overlay shows a note). */
async function fetchMySubmission(toolId: string): Promise<MySubmissionDetail | null> {
  try {
    const res = await fetch(`/api/me/tools/${encodeURIComponent(toolId)}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) return null;
    return (await res.json()) as MySubmissionDetail;
  } catch {
    return null;
  }
}

/** The owner-editable fields; only changed ones are sent. */
interface EditSubmissionPatch {
  name?: string;
  description?: string;
  problemSolved?: string;
  setupInstructions?: string;
  dependencies?: string;
  connectorSetupNotes?: string | null;
  /**
   * Edited per-step explanations, keyed by component id — only the steps the owner changed. The
   * backend applies each onto the stored structured docs (never adding/removing a step) and re-derives
   * the flat setup_instructions. Sent instead of `setupInstructions` for a tool that HAS structured docs.
   */
  stepExplanations?: Record<string, string>;
}

/**
 * PATCH an edit to one of the owner's tools. The backend applies the re-review rule and returns the
 * resulting status + whether the edit re-queued the tool for the AI SWAT team. `invalid` carries the
 * field names the server rejected so the form can point at them.
 */
async function patchMySubmission(
  toolId: string,
  patch: EditSubmissionPatch,
): Promise<
  | { ok: true; publicationStatus: string; requeued: boolean }
  | { ok: false; reason: 'invalid'; detail: string[] }
  | { ok: false; reason: 'not_found' | 'error' }
> {
  const res = await fetch(`/api/me/tools/${encodeURIComponent(toolId)}`, {
    method: 'PATCH',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(patch),
  });
  if (res.ok) {
    const body = (await res.json()) as { publicationStatus: string; requeued: boolean };
    return { ok: true, publicationStatus: body.publicationStatus, requeued: body.requeued };
  }
  if (res.status === 404) return { ok: false, reason: 'not_found' };
  if (res.status === 400) {
    const body = (await res.json().catch(() => null)) as { detail?: unknown } | null;
    const detail = Array.isArray(body?.detail) ? (body!.detail as string[]) : [];
    return { ok: false, reason: 'invalid', detail };
  }
  return { ok: false, reason: 'error' };
}

/** Withdraw (true) or Restore (false) one of the owner's tools. Returns ok on 200, else an error. */
async function setMySubmissionWithdrawn(
  toolId: string,
  withdrawn: boolean,
): Promise<{ ok: true } | { ok: false }> {
  const res = await fetch(
    `/api/me/tools/${encodeURIComponent(toolId)}/${withdrawn ? 'withdraw' : 'restore'}`,
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: '{}',
    },
  );
  return res.ok ? { ok: true } : { ok: false };
}

/** A square crop region in the SOURCE image's natural pixel coordinates. */
interface CropRect {
  x: number;
  y: number;
  size: number;
}

/** Read a Blob as a data: URI string. */
function blobToDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(blob);
  });
}

/** Promisify canvas.toBlob (which passes null on failure). */
function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, quality));
}

/**
 * Re-encode the chosen crop of a loaded image into a small square data: URI, client-side (Stage 5.2
 * avatar slice 2). This is where the "no file exceeds the maximum" guarantee starts: we draw the
 * crop into a fixed AVATAR_OUTPUT_PX square canvas (so dimensions are bounded regardless of the
 * source), then encode to WebP, stepping the quality down until the result is within
 * AVATAR_OUTPUT_MAX_BYTES. Re-encoding through a canvas also DISCARDS EXIF/metadata as a side
 * effect (a privacy nicety — no embedded GPS/camera data ever leaves the browser).
 *
 * Returns the data: URI, or throws if the browser could not encode (extremely unlikely for a 256px
 * square) or if even the lowest quality still exceeds the cap.
 */
async function cropToDataUri(img: HTMLImageElement, crop: CropRect): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_OUTPUT_PX;
  canvas.height = AVATAR_OUTPUT_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no-2d-context');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    img,
    crop.x,
    crop.y,
    crop.size,
    crop.size,
    0,
    0,
    AVATAR_OUTPUT_PX,
    AVATAR_OUTPUT_PX,
  );
  // Prefer WebP (smallest); step quality down to fit the cap. A 256² photo is tiny — the first
  // step almost always fits — but the loop keeps the guarantee even for pathological content.
  for (const quality of [0.9, 0.8, 0.7, 0.6, 0.5]) {
    const blob = await canvasToBlob(canvas, 'image/webp', quality);
    // Some engines ignore an unsupported type and fall back to PNG; that PNG is still an allowlisted
    // MIME, so accept it if it happens to fit.
    if (blob && blob.size <= AVATAR_OUTPUT_MAX_BYTES) return blobToDataUri(blob);
  }
  throw new Error('too-large');
}

export const PROFILE_STYLES = `
  .tb-profile { max-width: 46rem; }
  .tb-profile__head {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
  .tb-avatar {
    width: 72px; height: 72px;
    border-radius: 50%;
    object-fit: cover;
    border: 1px solid var(--tb-line);
    background: var(--tb-surface);
    flex: 0 0 auto;
  }
  .tb-avatar--fallback {
    display: flex; align-items: center; justify-content: center;
    font-size: 1.75rem; font-weight: 700;
    color: var(--tb-accent-ink); background: var(--tb-accent);
  }
  .tb-profile section { margin-top: 2rem; }
  .tb-profile h2 { font-size: 1.05rem; margin-bottom: 0.75rem; }
  .tb-profile dl {
    display: grid;
    grid-template-columns: minmax(8rem, max-content) 1fr;
    gap: 0.5rem 1.25rem;
    margin: 0;
  }
  .tb-profile dt { color: var(--tb-muted); font-weight: 600; }
  .tb-profile dd { margin: 0; }
  .tb-profile__unset { color: var(--tb-muted); font-style: italic; }
  .tb-profile__synced { color: var(--tb-muted); font-size: 0.8125rem; margin-top: 1.5rem; }
  .tb-profile__note { color: var(--tb-muted); font-size: 0.8125rem; }
  .tb-profile ul { margin: 0; padding-left: 1.25rem; }
  .tb-profile__badges { list-style: none; padding: 0; display: flex; flex-wrap: wrap; gap: 0.4rem; }
  .tb-profile__badges li {
    background: var(--tb-surface); border: 1px solid var(--tb-line);
    border-radius: 999px; padding: 0.15rem 0.65rem; font-size: 0.875rem;
  }
  /* Displayed-badge strip (Stage 5.3). A DISTINCT class from the skills/interests pills above so
     the two never collide. A slightly stronger chip — the accent border marks it as an earned
     badge, not a free-text skill — but still just a plain list (no number, no score). */
  .tb-profile__achievements {
    list-style: none; padding: 0; margin: 0 0 0.5rem;
    display: flex; flex-wrap: wrap; gap: 0.5rem;
  }
  .tb-profile__achievements li {
    background: var(--tb-surface);
    border: 1px solid var(--tb-accent);
    border-radius: 999px; padding: 0.2rem 0.7rem;
    font-size: 0.875rem; font-weight: 600;
  }
  /* "View more" toggle beneath a public profile's featured badges — a plain link-style button, NEVER
     a count (PC-F31). Used by PersonProfile's badges section; the owner's own strip never shows it. */
  .tb-profile__viewmore {
    margin-top: 0.4rem; padding: 0; border: none; background: none;
    color: var(--tb-accent); font: inherit; font-size: 0.875rem; font-weight: 600; cursor: pointer;
  }
  .tb-profile__viewmore:hover { text-decoration: underline; }
  .tb-profile__viewmore:focus-visible { outline: 2px solid var(--tb-ink); outline-offset: 2px; }
  /* Badges section head — the "Your badges" heading beside its "Choose badges" affordance (Slice 2). */
  .tb-profile__badges-head {
    display: flex; align-items: baseline; justify-content: space-between;
    gap: 1rem; margin-bottom: 0.5rem;
  }
  /* The quiet "new badge" affordance (PR-F46 / NT §8 — a cue, never a notification or a count).
     A small accent dot on the "Choose badges" button when the person has earned badges they have
     not yet looked at; it clears once they open the panel. No number is ever shown. */
  .tb-badge-new-dot {
    display: inline-block; width: 0.5rem; height: 0.5rem; margin-left: 0.4rem;
    border-radius: 999px; background: var(--tb-accent); vertical-align: middle;
  }
  /* The matching per-row "new" tag inside the panel — a small word beside a just-earned badge's
     name, cleared next time (the panel marks badges seen on open). Not a count, not a score. */
  .tb-badge-picker__new {
    margin-left: 0.4rem; padding: 0.02rem 0.35rem;
    font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
    color: var(--tb-bg); background: var(--tb-accent); border-radius: 999px;
    vertical-align: middle;
  }
  /* Badge picker inside the panel (Slice 2/3). Rows are grouped under the §5.2 section headings
     (Contributing, Reusing, …). Earned rows are switches toggling display; not-yet-earned rows are
     muted and non-interactive, showing how to earn them. No number/score/total anywhere (PR-F58 /
     UR-F22 / PC-F31) — just names, criterion wording, and a shown/hidden state. */
  .tb-badge-picker { list-style: none; padding: 0; margin: 0; }
  /* One §5.2 section: a heading + its gloss, then that group's rows. */
  .tb-badge-group { margin: 0 0 1rem; }
  .tb-badge-group:last-child { margin-bottom: 0; }
  .tb-badge-group__head {
    position: sticky; top: 0; z-index: 1;
    margin: 0; padding: 0.35rem 0 0.3rem;
    background: var(--tb-bg);
    border-bottom: 1px solid var(--tb-line);
  }
  .tb-badge-group__title {
    font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
    color: var(--tb-ink);
  }
  .tb-badge-group__gloss { color: var(--tb-muted); font-size: 0.75rem; font-weight: 400; }
  .tb-badge-group ul { list-style: none; padding: 0; margin: 0.4rem 0 0; }
  .tb-badge-picker li { margin: 0 0 0.4rem; }
  .tb-badge-picker li:last-child { margin-bottom: 0; }
  .tb-badge-picker__row {
    /* THREE columns now, not two: a leading medallion, then the name/criterion stack, then the state.
       The icon spans both rows so it centres against the pair. */
    display: grid; grid-template-columns: auto 1fr auto; gap: 0.1rem 0.75rem; align-items: center;
    width: 100%; text-align: left; font: inherit; cursor: pointer;
    padding: 0.5rem 0.7rem; border: 1px solid var(--tb-line); border-radius: 8px;
    background: var(--tb-surface); color: var(--tb-ink);
    transition: border-color 0.12s ease, background-color 0.12s ease;
  }
  .tb-badge-picker__row:hover:not(:disabled) { border-color: var(--tb-accent); }
  .tb-badge-picker__row[aria-checked='true'] {
    border-color: var(--tb-accent); border-left-width: 3px;
  }
  .tb-badge-picker__row:focus-visible { outline: 2px solid var(--tb-ink); outline-offset: 2px; }
  .tb-badge-picker__row:disabled { opacity: 0.55; cursor: default; }
  /* Not-yet-earned rows (Slice 3, PR-F44): visibly muted and non-interactive. A plain div, not a
     switch — nothing to toggle until the badge is earned; the criterion (PR-F45) shows how to earn it. */
  .tb-badge-picker__row--locked {
    opacity: 0.6; cursor: default; border-style: dashed; border-color: var(--tb-line);
    background: transparent;
  }
  .tb-badge-picker__icon { grid-column: 1; grid-row: 1 / span 2; width: 42px; }
  .tb-badge-picker__name { grid-column: 2; font-weight: 600; }
  .tb-badge-picker__criterion { grid-column: 2; color: var(--tb-muted); font-size: 0.8125rem; }
  .tb-badge-picker__state {
    grid-column: 3; grid-row: 1 / span 2;
    font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em;
    color: var(--tb-muted); white-space: nowrap;
  }
  .tb-badge-picker__row[aria-checked='true'] .tb-badge-picker__state { color: var(--tb-accent-ink); }
  .tb-private {
    border: 1px solid var(--tb-line);
    border-left: 3px solid var(--tb-accent);
    border-radius: 6px;
    padding: 0.75rem 1rem;
  }
  .tb-private__marker {
    display: inline-block; font-size: 0.75rem; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.03em;
    color: var(--tb-accent-ink); background: var(--tb-accent);
    border-radius: 4px; padding: 0.1rem 0.4rem; margin-bottom: 0.5rem;
  }
  /* The private "Who I follow" list (PR-F61). A plain, unranked list of people the owner follows: a
     small avatar + a name that links to that person's public profile, and an Unfollow control. Never
     ordered by any count (ordered by name server-side); no numbers on the rows (PC-F31). */
  .tb-follow-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.5rem; }
  .tb-follow-row { display: flex; align-items: center; gap: 0.6rem; }
  .tb-follow-row__avatar {
    width: 32px; height: 32px; border-radius: 50%; object-fit: cover;
    border: 1px solid var(--tb-line); background: var(--tb-surface); flex: 0 0 auto;
  }
  .tb-follow-row__avatar--fallback {
    display: flex; align-items: center; justify-content: center;
    font-size: 0.8rem; font-weight: 700; color: var(--tb-accent-ink); background: var(--tb-accent);
  }
  .tb-follow-row__name { flex: 1 1 auto; }
  .tb-follow-row__name a { color: var(--tb-accent); }
  /* The owner's own follower count: a bare read-only number (PR-F52). No list of who, no ranking. */
  .tb-follower-count { color: var(--tb-muted); font-size: 0.9rem; margin: 0.25rem 0 0; }
  /* PR-F38 activity figures on the builder's own tool. Deliberately QUIET — muted, small, no chip
     border, nothing to make a figure look like an award — because these are four named counts and not a
     score (UR-F22 / PC-F31). Separated by a middot rather than laid out as a table, so no column
     invites a reader to line two tools up against each other. */
  .tb-activity {
    list-style: none; padding: 0; margin: 0.15rem 0 0;
    display: flex; flex-wrap: wrap; gap: 0.15rem 0.75rem;
    color: var(--tb-muted); font-size: 0.8125rem;
  }
  .tb-activity li + li::before { content: '·'; margin-right: 0.75rem; }
  /* PR-F35 / CH-F13 — the raiser's own support requests. One card per request: the status, what they
     asked, then the recorded history. The history is a plain ordered list of status moves and reasons;
     the triager's NAME is deliberately not among them (FB-F16 / NT-F17). */
  .tb-request-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.85rem; }
  .tb-request {
    border: 1px solid var(--tb-line); border-radius: 6px; padding: 0.6rem 0.8rem;
    background: var(--tb-surface);
  }
  .tb-request__head {
    display: flex; flex-wrap: wrap; align-items: baseline; gap: 0.5rem; margin-bottom: 0.35rem;
  }
  .tb-request__status {
    font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em;
    color: var(--tb-accent-ink); background: var(--tb-accent);
    border-radius: 4px; padding: 0.1rem 0.45rem;
  }
  .tb-request__meta { color: var(--tb-muted); font-size: 0.8125rem; }
  .tb-request__body { margin: 0; white-space: pre-wrap; }
  .tb-request__history { margin: 0.5rem 0 0; padding-left: 1.1rem; font-size: 0.8125rem; }
  .tb-request__history li + li { margin-top: 0.25rem; }
  .tb-request__reason { color: var(--tb-ink); }
  .tb-signin-btn {
    display: inline-block;
    margin-top: 1rem;
    padding: 0.55rem 1rem;
    background: var(--tb-accent);
    color: var(--tb-accent-ink);
    text-decoration: none;
    border-radius: 6px;
    font-weight: 600;
  }
  .tb-signin-btn:focus-visible { outline: 2px solid var(--tb-ink); outline-offset: 2px; }
  .tb-btn {
    font: inherit; cursor: pointer;
    padding: 0.35rem 0.8rem;
    border: 1px solid var(--tb-line); border-radius: 6px;
    background: var(--tb-surface); color: var(--tb-ink);
  }
  .tb-btn:focus-visible { outline: 2px solid var(--tb-ink); outline-offset: 2px; }
  .tb-btn--primary { background: var(--tb-accent); color: var(--tb-accent-ink); border-color: var(--tb-accent); }
  .tb-btn--small { padding: 0.2rem 0.6rem; font-size: 0.8125rem; }
  .tb-btn:disabled { opacity: 0.6; cursor: default; }
  .tb-bio-edit { display: flex; flex-direction: column; gap: 0.5rem; margin-top: 0.75rem; }
  .tb-bio-edit textarea {
    font: inherit; width: 100%; min-height: 5rem; resize: vertical;
    padding: 0.5rem; border: 1px solid var(--tb-line); border-radius: 6px;
    background: var(--tb-bg); color: var(--tb-ink);
  }
  .tb-bio-edit__row { display: flex; align-items: center; gap: 0.75rem; }
  .tb-bio-edit__count { color: var(--tb-muted); font-size: 0.8125rem; }
  .tb-bio-edit__count--over { color: #b00020; font-weight: 600; }
  .tb-bio-edit__error { color: #b00020; font-size: 0.8125rem; margin: 0; }

  /* Avatar edit panel (Stage 5.2 avatar slice). The edit affordance itself now lives on the new
     band as .pp-avatar-edit (see profilePage.css); the modal/cropper styles below are unchanged. */
  .tb-modal__backdrop {
    position: fixed; inset: 0; z-index: 50;
    background: rgba(0, 0, 0, 0.45);
    display: flex; align-items: center; justify-content: center;
    padding: 1rem;
  }
  .tb-modal {
    background: var(--tb-bg); color: var(--tb-ink);
    border: 1px solid var(--tb-line); border-radius: 10px;
    padding: 1.25rem 1.5rem; max-width: 26rem; width: 100%;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
  }
  .tb-modal h2 { margin: 0 0 0.75rem; font-size: 1.05rem; }
  /* The "Edit your profile" popup gathers avatar + bio + self-declared, so it can outgrow the
     viewport. Cap it and let the whole body scroll (it has no fixed header/footer split, unlike
     --tall). Section subheads separate avatar / bio / about / certifications inside it. */
  .tb-modal--scroll { max-height: min(85vh, 44rem); overflow-y: auto; }
  .tb-edit-profile__subhead {
    margin: 1.25rem 0 0.25rem; font-size: 0.95rem; font-weight: 600; color: var(--tb-ink);
  }
  /* A tall modal (the badge panel) becomes a fixed header + scrolling body + fixed footer, capped to
     the viewport so it never fills the whole screen. The body scrolls; heading and Done stay put. */
  .tb-modal--tall {
    display: flex; flex-direction: column;
    max-height: min(80vh, 40rem); padding-bottom: 1rem;
  }
  .tb-modal--tall > h2 { flex: 0 0 auto; }
  .tb-modal__intro { flex: 0 0 auto; margin: 0 0 0.5rem; }
  .tb-modal__scroll {
    flex: 1 1 auto; overflow-y: auto; min-height: 0;
    margin: 0 -0.5rem; padding: 0.25rem 0.5rem;
  }
  .tb-modal__foot { flex: 0 0 auto; padding-top: 0.75rem; border-top: 1px solid var(--tb-line); }
  /* The public-visibility opt-out row inside the badge panel. Checkbox + label, hint beneath. */
  .tb-badge-privacy {
    display: flex; align-items: flex-start; gap: 0.55rem;
    margin: 0 0 0.75rem; font-size: 0.9rem; cursor: pointer;
  }
  .tb-badge-privacy input { margin-top: 0.2rem; flex: 0 0 auto; }
  .tb-badge-privacy > span { display: flex; flex-direction: column; gap: 0.15rem; }
  .tb-badge-privacy__hint { margin: 0; }
  /* The contribution-summary chooser (UR-F21/UR-F33): six checkbox rows, one per figure in the closed
     set, each showing the figure's own current value so the person can see what publishing it would
     say. Deliberately NOT the badge-picker switch styling — a badge switch means "show a thing I
     earned", this means "publish a number about me", and the two should not look interchangeable.
     The value sits in its own column and is never totalled, ordered or compared (UR-F22/PC-F31). */
  .tb-figure-picker { list-style: none; padding: 0; margin: 0.5rem 0 0; }
  .tb-figure-picker li { margin: 0 0 0.4rem; }
  .tb-figure-picker li:last-child { margin-bottom: 0; }
  .tb-figure-row {
    display: grid; grid-template-columns: auto 1fr auto; gap: 0.15rem 0.6rem;
    align-items: baseline; padding: 0.5rem 0.7rem; font-size: 0.9rem; cursor: pointer;
    border: 1px solid var(--tb-line); border-radius: 8px; background: var(--tb-surface);
  }
  .tb-figure-row:hover { border-color: var(--tb-accent); }
  .tb-figure-row--on { border-color: var(--tb-accent); border-left-width: 3px; }
  .tb-figure-row input { margin: 0; align-self: center; }
  .tb-figure-row__value { color: var(--tb-muted); font-variant-numeric: tabular-nums; }
  /* A per-row note (today only the badge-privacy conflict): full width, under the label. */
  .tb-figure-row__note { grid-column: 2 / -1; margin: 0; }
  /* The read-only rendering of somebody's CHOSEN figures on the owner's own page and on
     PersonProfile — a plain definition list of name + number. No chart, no bar, no share of a
     total: a figure here is a fact about one person, not a position among people (PC-F31). */
  .tb-figures { display: flex; flex-wrap: wrap; gap: 0.5rem 1.5rem; margin: 0; padding: 0; }
  .tb-figures > div { display: flex; flex-direction: column; }
  .tb-figures dt { color: var(--tb-muted); font-size: 0.8125rem; }
  .tb-figures dd { margin: 0; font-size: 1.05rem; font-weight: 600; }
  .tb-avatar-choices { display: flex; flex-wrap: wrap; gap: 0.75rem; margin: 0.5rem 0 1rem; padding: 0; }
  .tb-avatar-choice {
    padding: 3px; border: 2px solid transparent; border-radius: 50%;
    background: none; cursor: pointer; line-height: 0;
  }
  .tb-avatar-choice img {
    width: 64px; height: 64px; border-radius: 50%; object-fit: cover;
    border: 1px solid var(--tb-line); background: var(--tb-surface); display: block;
  }
  .tb-avatar-choice[aria-pressed='true'] { border-color: var(--tb-accent); }
  .tb-avatar-choice:focus-visible { outline: 2px solid var(--tb-ink); outline-offset: 2px; }
  .tb-modal__row { display: flex; flex-wrap: wrap; align-items: center; gap: 0.75rem; margin-top: 0.5rem; }
  .tb-modal__error { color: #b00020; font-size: 0.8125rem; margin: 0.5rem 0 0; }

  /* Upload + cropper (Stage 5.2 avatar slice 2). */
  .tb-upload-btn {
    position: relative; overflow: hidden;
    display: inline-flex; align-items: center; gap: 0.4rem;
  }
  .tb-upload-btn input[type='file'] {
    position: absolute; inset: 0; opacity: 0; cursor: pointer;
    font-size: 0; /* keep the native control out of the layout */
  }
  .tb-crop {
    position: relative; margin: 0.75rem 0; width: 100%;
    touch-action: none; user-select: none; -webkit-user-select: none;
    background: var(--tb-surface); border: 1px solid var(--tb-line); border-radius: 8px;
    display: flex; align-items: center; justify-content: center; overflow: hidden;
  }
  .tb-crop img { display: block; max-width: 100%; max-height: 340px; pointer-events: none; }
  .tb-crop__box {
    position: absolute; box-sizing: border-box;
    border: 2px solid #fff; border-radius: 50%;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.45);
    cursor: move;
  }
  .tb-crop__handle {
    position: absolute; width: 16px; height: 16px;
    right: -8px; bottom: -8px;
    background: #fff; border: 1px solid var(--tb-line); border-radius: 50%;
    cursor: nwse-resize;
  }
  .tb-crop__hint { color: var(--tb-muted); font-size: 0.8125rem; margin: 0.25rem 0 0; }

  /* Submission tracker + Tool Overlay (Will's ProfileFlow "clicks on a submitted tool"). */
  /* A link-styled button so a shared-tool row opens the overlay while reading as a link (it is an
     action, not navigation, so it's a <button> for correct semantics/keyboard support). */
  .tb-linkbtn {
    font: inherit; padding: 0; border: 0; background: none; cursor: pointer;
    color: var(--tb-accent); text-decoration: underline;
  }
  .tb-linkbtn:focus-visible { outline: 2px solid var(--tb-ink); outline-offset: 2px; }
  /* The Tool Overlay is taller than the small avatar/bio modals — let it scroll within the viewport. */
  #submission-overlay-heading { outline: none; }
  .tb-modal[aria-labelledby='submission-overlay-heading'] {
    max-width: 34rem; max-height: min(85vh, 48rem); overflow-y: auto;
  }
  /* A bordered callout used for the reviewer's reason, the re-review warning, and save notices. */
  .tb-callout {
    border: 1px solid var(--tb-line); border-left: 3px solid var(--tb-accent);
    border-radius: 8px; padding: 0.6rem 0.8rem; margin: 0.75rem 0;
    background: var(--tb-surface); font-size: 0.9rem;
  }
  .tb-callout p { font-size: inherit; }
  .tb-callout--warn { border-left-color: #b8860b; }
  .tb-callout--error { border-left-color: #b00020; color: #b00020; }
  /* "Withdrawn from catalogue" pill shown next to a tool name (list row + overlay heading) so the
     owner can see at a glance that a tool is hidden from the catalogue. Muted amber, not alarming. */
  .tb-withdrawn-tag {
    display: inline-block;
    margin-left: 0.4rem;
    padding: 0.05rem 0.5rem;
    border: 1px solid #b8860b;
    border-radius: 999px;
    background: #fff8e6;
    color: #7a5c00;
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1.5;
    vertical-align: middle;
    white-space: nowrap;
  }
  .tb-fieldset {
    border: 1px solid var(--tb-line); border-radius: 8px;
    padding: 0.75rem 0.9rem 0.9rem; margin: 0.9rem 0 0;
  }
  .tb-fieldset > legend { font-weight: 600; padding: 0 0.4rem; }
  .tb-field { display: flex; flex-direction: column; gap: 0.3rem; margin-top: 0.7rem; }
  .tb-field:first-of-type { margin-top: 0.3rem; }
  .tb-field__label { font-size: 0.85rem; font-weight: 600; }
  .tb-field__input {
    font: inherit; width: 100%; resize: vertical;
    padding: 0.45rem 0.55rem; border: 1px solid var(--tb-line); border-radius: 6px;
    background: var(--tb-bg); color: var(--tb-ink);
  }
  .tb-field__input[aria-invalid='true'] { border-color: #b00020; }
  .tb-field__input:focus-visible { outline: 2px solid var(--tb-ink); outline-offset: 1px; }
  .tb-btn--danger { border-color: #b00020; color: #b00020; }
  .tb-btn--danger:hover:not(:disabled) { background: #b00020; color: #fff; }
  .tb-confirm { display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem; font-size: 0.9rem; }

  /* ── Boxed per-step setup editor (mirrors upload/InstructionsStage; header is read-only here) ── */
  .tb-comp-list { list-style: none; margin: 0.3rem 0 0; padding: 0; }
  .tb-comp {
    border: 1px solid var(--tb-line); border-radius: 6px;
    margin: 0 0 0.6rem; background: var(--tb-bg);
  }
  /* Nesting is shown by indentation AND by the branch label on the header, never by indent alone. */
  .tb-comp[data-depth="1"] { margin-left: 1.25rem; }
  .tb-comp[data-depth="2"] { margin-left: 2.5rem; }
  .tb-comp[data-depth="3"] { margin-left: 3.75rem; }
  .tb-comp[data-depth="4"] { margin-left: 5rem; }
  .tb-comp[data-depth="5"], .tb-comp[data-deep="true"] { margin-left: 6rem; }
  .tb-comp[data-missing="true"] { border-left: 4px solid #b3261e; }
  .tb-comp[data-missing="false"] { border-left: 4px solid #2e7d32; }
  .tb-comp-head {
    display: flex; align-items: baseline; gap: 0.5rem; flex-wrap: wrap;
    width: 100%; text-align: left;
    background: var(--tb-surface); border-radius: 5px 5px 0 0;
    padding: 0.55rem 0.7rem; color: var(--tb-ink);
  }
  /* The header carries no control in the profile editor — the step's identity is fixed by the flow. */
  .tb-comp-head--static { cursor: default; }
  .tb-comp-num { font-weight: 700; font-variant-numeric: tabular-nums; }
  .tb-comp-label { font-weight: 600; }
  .tb-comp-branch {
    font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.03em;
    color: var(--tb-muted); border: 1px solid var(--tb-line);
    border-radius: 999px; padding: 0.05rem 0.45rem; background: var(--tb-bg);
  }
  .tb-comp-meta { display: block; width: 100%; color: var(--tb-muted); font-size: 0.82rem; font-weight: 400; }
  .tb-comp-state { margin-left: auto; font-size: 0.82rem; font-weight: 600; white-space: nowrap; }
  .tb-comp-state[data-missing="true"] { color: #b3261e; }
  .tb-comp-state[data-missing="false"] { color: #2e7d32; }
  .tb-comp-body { padding: 0.6rem 0.7rem 0.75rem; }
  .tb-comp-body textarea { min-height: 5rem; }
  .tb-comp-body textarea[aria-invalid="true"] { border-color: #b3261e; }
  .tb-field-hint { color: var(--tb-muted); font-size: 0.85rem; }
  .tb-err { color: #b3261e; font-size: 0.82rem; }
`;

/* NOTE: this page used to declare its OWN `Field` here, rendering a visible "Not provided by the
 * directory" note for every empty value. Removed in the staging merge, and not by preference: the
 * shared ./people/fields helper (imported at the top of this file) exists specifically to replace it —
 * its header names this copy as the offender UR-F04 targets, since the requirement says an empty
 * directory item must be HIDDEN, not annotated. Keeping both would also have been a duplicate
 * identifier. Nothing imports `Field` from this module: PersonProfile takes it from ./people/fields. */

/** Format an ISO timestamp for display, or null if absent. */
function when(iso: string | null): string | null {
  return iso ? new Date(iso).toLocaleString() : null;
}

/**
 * True when at least one of these values would actually render a row through `Field` — the
 * SECTION-level companion to that helper's row-level rule (UR-F04).
 *
 * `Field` hides an empty row, so a wholly sparse account would otherwise leave a heading standing over
 * an empty `<dl>`: the same visible hole the requirement closes, moved up one level. Every `<dl>` on
 * this page is therefore gated on this, and the test has to match `Field`'s own — whitespace counts as
 * empty, or a directory field holding `" "` keeps a section alive that renders nothing inside it.
 * `people/PersonProfile.tsx` computes the same predicate inline for its Organisation block.
 */
function anyFieldFilled(values: (string | null | undefined)[]): boolean {
  return values.some((v) => typeof v === 'string' && v.trim() !== '');
}

/** A pill list (interests, skills), or nothing when empty. */
export function Pills({ label, items }: { label: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <>
      <dt>{label}</dt>
      <dd>
        <ul className="tb-profile__badges">
          {items.map((it) => (
            <li key={it}>{it}</li>
          ))}
        </ul>
      </dd>
    </>
  );
}

/**
 * A figure from an API response, or null when the response did not carry a usable one.
 *
 * A response is UNTRUSTED DATA even when the type says otherwise: these mirrors are hand-written, so a
 * server that stopped sending `activity` would leave `undefined` flowing into JSX and the row would read
 * "undefined adoptions" with `tsc` exiting 0 on both sides. Same guard, same reason, as
 * `followerCountOf` in `people/PersonProfile.tsx`. Negative and fractional values are refused rather
 * than rounded: a count that is neither is a server fault, and printing it would launder the fault.
 */
function figure(n: unknown): number | null {
  return typeof n === 'number' && Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
}

/** "1 adoption" / "3 adoptions" — the noun agrees with the number, in every one of these figures. */
function countLabel(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/**
 * PR-F38 — the four activity figures behind a builder's own tool, rendered ONCE for both places that
 * show them: the submission-list row and the Tool Overlay. The requirement is one sentence ("its
 * adoption count, like count, comments, open issues… and never who adopted, saved, liked or viewed it")
 * and the two surfaces were drifting apart before this existed, so there is deliberately no `variant`
 * prop: if the overlay ever needs to say something different from the row, that is a product decision
 * to make in one place, not a second copy of these four labels.
 *
 * TOGETHER WITH the leaderboard position already on the row, this is the whole recognition view: the
 * position answers "where am I", these answer "why".
 *
 * ZEROS ARE PRINTED, not hidden. The server sends real zeros for a tool nothing has happened to yet and
 * says why: unlike a cohort aggregate these count the builder's OWN tool's events, so "nobody has
 * adopted it yet" is a true and disclosable fact. Hiding them would leave a builder unable to tell "no
 * adoptions" from "the figure failed to load" — the ambiguity PR-NFR09's no-completion-meter rule is
 * NOT asking for. What PR-NFR09 rules out is a prompt or a score, and there is neither here: four named
 * counts, no total, no composite, and nothing compared against another person (UR-F22 / PC-F31).
 *
 * Renders NOTHING when the object is absent, or when every figure in it fails the `figure` guard — a
 * broken payload must leave the row exactly as it was, not print four "undefined"s.
 *
 * OWNER-ONLY. These are the BUILDER's figures for their OWN tool, shown on their own page. Nothing on a
 * public profile or a catalogue page should import this: PR-F38 is about what a builder sees about their
 * own work, and a public copy of it would be a per-person figure on somebody else's screen.
 */
export function ToolActivity({ activity }: { activity: ToolActivityFigures | null | undefined }) {
  if (activity == null) return null;
  const parts = [
    { key: 'adoptions', n: figure(activity.adoptions), one: 'adoption', many: 'adoptions' },
    { key: 'likes', n: figure(activity.likes), one: 'like', many: 'likes' },
    { key: 'comments', n: figure(activity.comments), one: 'comment', many: 'comments' },
    { key: 'openIssues', n: figure(activity.openIssues), one: 'open issue', many: 'open issues' },
  ].filter((p): p is { key: string; n: number; one: string; many: string } => p.n !== null);
  if (parts.length === 0) return null;
  return (
    <ul className="tb-activity" aria-label="This tool's activity">
      {parts.map((p) => (
        <li key={p.key}>{countLabel(p.n, p.one, p.many)}</li>
      ))}
    </ul>
  );
}

/**
 * An interactive square cropper (Stage 5.2 avatar slice 2). Shows the loaded source image with a
 * draggable + resizable circular crop selection overlaid; the parent gets the crop rect (in the
 * image's NATURAL pixel coordinates) on every change so it can re-encode on confirm. Pure pointer
 * events over an <img> — no dependency. The overlay renders in DISPLAY pixels; we scale to natural
 * pixels via the rendered-vs-natural ratio so the crop is correct regardless of how the browser
 * sized the image to fit.
 */
function AvatarCropper({
  src,
  onCropChange,
}: {
  src: string;
  onCropChange: (crop: CropRect) => void;
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  // Crop box in DISPLAY pixels relative to the rendered image top-left.
  const [box, setBox] = useState<{ x: number; y: number; size: number } | null>(null);
  // The rendered image geometry (offset within the container + display/natural scale).
  const geomRef = useRef<{
    left: number;
    top: number;
    dispW: number;
    dispH: number;
    scale: number;
  } | null>(null);
  const dragRef = useRef<{
    mode: 'move' | 'resize';
    px: number;
    py: number;
    box: { x: number; y: number; size: number };
  } | null>(null);

  // Once the image lays out, measure it and seed a centred square crop (the largest that fits).
  function measureAndInit() {
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const parentRect = img.parentElement!.getBoundingClientRect();
    const left = rect.left - parentRect.left;
    const top = rect.top - parentRect.top;
    const scale = img.naturalWidth / rect.width; // natural px per display px
    geomRef.current = { left, top, dispW: rect.width, dispH: rect.height, scale };
    const size = Math.min(rect.width, rect.height);
    const seeded = { x: (rect.width - size) / 2, y: (rect.height - size) / 2, size };
    setBox(seeded);
    emit(seeded);
  }

  // Translate a display-pixel box into a natural-pixel CropRect and hand it up.
  function emit(b: { x: number; y: number; size: number }) {
    const g = geomRef.current;
    if (!g) return;
    onCropChange({ x: b.x * g.scale, y: b.y * g.scale, size: b.size * g.scale });
  }

  function clamp(b: { x: number; y: number; size: number }): {
    x: number;
    y: number;
    size: number;
  } {
    const g = geomRef.current;
    if (!g) return b;
    const size = Math.max(32, Math.min(b.size, g.dispW, g.dispH));
    const x = Math.max(0, Math.min(b.x, g.dispW - size));
    const y = Math.max(0, Math.min(b.y, g.dispH - size));
    return { x, y, size };
  }

  function onPointerDown(mode: 'move' | 'resize', e: React.PointerEvent) {
    if (!box) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    dragRef.current = { mode, px: e.clientX, py: e.clientY, box };
  }

  function onPointerMove(e: React.PointerEvent) {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.px;
    const dy = e.clientY - drag.py;
    const next =
      drag.mode === 'move'
        ? { ...drag.box, x: drag.box.x + dx, y: drag.box.y + dy }
        : { ...drag.box, size: drag.box.size + Math.max(dx, dy) };
    const clamped = clamp(next);
    setBox(clamped);
    emit(clamped);
  }

  function onPointerUp(e: React.PointerEvent) {
    if (dragRef.current) {
      (e.target as Element).releasePointerCapture?.(e.pointerId);
      dragRef.current = null;
    }
  }

  return (
    <div className="tb-crop" onPointerMove={onPointerMove} onPointerUp={onPointerUp}>
      <img ref={imgRef} src={src} alt="" onLoad={measureAndInit} />
      {box && (
        <div
          className="tb-crop__box"
          style={{ left: box.x, top: box.y, width: box.size, height: box.size }}
          onPointerDown={(e) => onPointerDown('move', e)}
          role="presentation"
        >
          <span
            className="tb-crop__handle"
            onPointerDown={(e) => onPointerDown('resize', e)}
            role="presentation"
          />
        </div>
      )}
    </div>
  );
}

/**
 * The avatar chooser (Stage 5.2 avatar slices 1 + 2). A modal panel with two modes:
 *   - 'choose'  — pick a stock avatar, start a custom upload, or revert to the directory photo.
 *   - 'crop'    — after a file is picked: drag/resize a square crop, then Save (re-encodes to a
 *                 256×256 WebP data: URI capped at ~200 KB and PUTs it).
 * Owner-only, presentation-only (UR-F30) — reverting restores the Microsoft directory photo exactly,
 * because the backend never touched it.
 *
 * SECURITY: the uploaded image is cropped + re-encoded ENTIRELY in the browser (cropToDataUri) and
 * the resulting data: URI is validated again server-side; the original file is never uploaded. The
 * render path (resolveAvatar) independently re-validates any stored ref before it reaches <img src>.
 *
 * ACCESSIBILITY: role="dialog" + aria-modal; Escape and backdrop-click close it; focus moves into
 * the panel on open and returns to the trigger on close.
 */
function AvatarPanel({
  self,
  certifications,
  hasReplacement,
  onClose,
  returnFocusRef,
}: {
  self: SelfDeclared | null;
  certifications: MeResponse['certifications'];
  hasReplacement: boolean;
  onClose: () => void;
  returnFocusRef: React.RefObject<HTMLButtonElement>;
}) {
  const revalidator = useRevalidator();
  const currentStockId =
    self?.avatarKind === 'stock' && self.avatarBlobRef?.startsWith('stock:')
      ? self.avatarBlobRef.slice('stock:'.length)
      : null;
  const [selected, setSelected] = useState<string | null>(currentStockId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Cropper mode: the object URL of the picked file (revoked on leaving crop mode) + the live crop.
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<CropRect | null>(null);
  const cropImgRef = useRef<HTMLImageElement | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const firstBtnRef = useRef<HTMLButtonElement>(null);

  // Move focus into the panel on open; return it to the trigger on close (unmount).
  useEffect(() => {
    firstBtnRef.current?.focus();
    const trigger = returnFocusRef.current;
    return () => trigger?.focus();
  }, [returnFocusRef]);

  // Always revoke the object URL when it changes or the panel unmounts (no leaked blob handles).
  useEffect(() => {
    return () => {
      if (cropSrc) URL.revokeObjectURL(cropSrc);
    };
  }, [cropSrc]);

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    setError(null);
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file later
    if (!file) return;
    if (!AVATAR_ACCEPT.split(',').includes(file.type)) {
      setError('Please choose a PNG, JPEG or WebP image.');
      return;
    }
    if (file.size > AVATAR_ORIGINAL_MAX_BYTES) {
      setError('That image is too large. Please choose one under 5 MB.');
      return;
    }
    // Load the file into an off-DOM Image so the cropper can measure natural dimensions, and keep
    // an object URL to display it. Both the URL and the element live until we leave crop mode.
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      cropImgRef.current = img;
      setCrop(null);
      setCropSrc(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      setError('That image could not be read. Please try another file.');
    };
    img.src = url;
  }

  function leaveCrop() {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    cropImgRef.current = null;
    setCrop(null);
    setCropSrc(null);
    setError(null);
  }

  async function commit(
    body:
      | { kind: 'stock'; stockId: string }
      | { kind: 'upload'; dataUri: string }
      | { kind: 'directory' },
  ) {
    if (saving) return;
    setSaving(true);
    setError(null);
    const result = await putAvatar(body);
    if (result.ok) {
      revalidator.revalidate(); // re-run meLoader → re-reads the saved avatar
      onClose();
      return;
    }
    setSaving(false);
    setError(
      result.reason === 'invalid'
        ? 'That avatar could not be saved. Please try a different image.'
        : 'Something went wrong saving your avatar. Please try again.',
    );
  }

  async function saveCrop() {
    const img = cropImgRef.current;
    if (!img || !crop || saving) return;
    setSaving(true);
    setError(null);
    try {
      const dataUri = await cropToDataUri(img, crop);
      // commit() re-sets saving; call putAvatar via commit to reuse the shared result handling.
      setSaving(false);
      await commit({ kind: 'upload', dataUri });
    } catch (err) {
      setSaving(false);
      setError(
        (err as Error).message === 'too-large'
          ? 'That image is too detailed to store. Try a simpler or smaller photo.'
          : 'That image could not be processed. Please try another file.',
      );
    }
  }

  const inCrop = cropSrc !== null;

  return (
    <div
      className="tb-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        ref={panelRef}
        className={`tb-modal${inCrop ? '' : ' tb-modal--scroll'}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="avatar-panel-heading"
      >
        {inCrop ? (
          <>
            <h2 id="avatar-panel-heading">Crop your photo</h2>
            <p className="tb-profile__note" style={{ marginTop: 0 }}>
              Drag to reposition, or drag the handle to resize. Your photo is cropped and shrunk on
              your device — the original never leaves your browser.
            </p>
            <AvatarCropper src={cropSrc} onCropChange={setCrop} />
            <div className="tb-modal__row">
              <button
                type="button"
                ref={firstBtnRef}
                className="tb-btn tb-btn--primary"
                onClick={() => void saveCrop()}
                disabled={saving || !crop}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="tb-btn" onClick={leaveCrop} disabled={saving}>
                Back
              </button>
            </div>
            {error && <p className="tb-modal__error">{error}</p>}
          </>
        ) : (
          <>
            <h2 id="avatar-panel-heading">Edit your profile</h2>

            <h3 className="tb-edit-profile__subhead">Avatar</h3>
            <p className="tb-profile__note" style={{ marginTop: 0 }}>
              Pick a stock avatar, upload your own photo, or use the photo from your work directory.
            </p>
            <ul className="tb-avatar-choices">
              {[...STOCK_AVATARS.entries()].map(([id, { url, label }], i) => (
                <li key={id} style={{ listStyle: 'none' }}>
                  <button
                    type="button"
                    ref={i === 0 ? firstBtnRef : undefined}
                    className="tb-avatar-choice"
                    aria-pressed={selected === id}
                    aria-label={`${label} avatar`}
                    onClick={() => setSelected(id)}
                    disabled={saving}
                  >
                    <img src={url} alt="" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="tb-modal__row">
              <button
                type="button"
                className="tb-btn tb-btn--primary"
                onClick={() => selected && void commit({ kind: 'stock', stockId: selected })}
                disabled={saving || !selected}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <label className="tb-btn tb-upload-btn">
                <span aria-hidden="true">⬆</span> Upload a photo
                <input
                  type="file"
                  accept={AVATAR_ACCEPT}
                  onChange={onPickFile}
                  disabled={saving}
                  aria-label="Upload a photo from your device"
                />
              </label>
              {hasReplacement && (
                <button
                  type="button"
                  className="tb-btn"
                  onClick={() => void commit({ kind: 'directory' })}
                  disabled={saving}
                >
                  Use directory photo
                </button>
              )}
              <button type="button" className="tb-btn" onClick={onClose} disabled={saving}>
                Cancel
              </button>
            </div>
            {error && <p className="tb-modal__error">{error}</p>}

            {/* Bio (Stage 5.2 bio-only editable slice). Folded into this "Edit your profile" popup —
                the band displays the bio, and this is where the owner changes it. Saving revalidates
                the /me loader and closes the popup, matching the avatar save behaviour above. */}
            <h3 className="tb-edit-profile__subhead">Bio</h3>
            <p className="tb-profile__note" style={{ marginTop: 0 }}>
              Self-declared — set by you, not verified by the platform.
              {self?.selfSectionHidden ? ' This section is hidden from other people.' : ''}
            </p>
            <BioEditor initialBio={self?.bio ?? null} onDone={onClose} />

            {/* The rest of the self-declared profile is display-only this stage (no editor yet), but it
                belonged to the removed "About you" section — surfaced here read-only so nothing is lost. */}
            {self &&
              (self.interests.length > 0 ||
                self.skillsHave.length > 0 ||
                self.skillsWanted.length > 0 ||
                self.competencyLevel != null ||
                self.makerType != null) && (
                <>
                  <h3 className="tb-edit-profile__subhead">About you</h3>
                  <dl>
                    <Pills label="Interests" items={self.interests} />
                    <Pills label="Skills you have" items={self.skillsHave} />
                    <Pills label="Skills you want" items={self.skillsWanted} />
                    {self.competencyLevel && (
                      <Field label="Competency (self-assessed)" value={self.competencyLevel} />
                    )}
                    {self.makerType && <Field label="Maker type" value={self.makerType} />}
                  </dl>
                </>
              )}

            {certifications.length > 0 && (
              <>
                <h3 className="tb-edit-profile__subhead">Certifications</h3>
                <p className="tb-profile__note" style={{ marginTop: 0 }}>
                  Self-declared — not verified by the platform.
                </p>
                <ul>
                  {certifications.map((c) => (
                    <li key={c.ordinal}>
                      {c.name} ({c.year})
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The bio edit form (Stage 5.2, bio-only slice). Controlled textarea + Save/Cancel, mirroring the
 * server cap client-side for immediate feedback (the server + DB CHECK remain the source of truth).
 * On a successful save it revalidates the /me loader so the page re-reads the stored bio.
 */
function BioEditor({ initialBio, onDone }: { initialBio: string | null; onDone: () => void }) {
  const revalidator = useRevalidator();
  const [value, setValue] = useState(initialBio ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const over = value.trim().length > BIO_MAX;

  async function save() {
    if (over || saving) return;
    setSaving(true);
    setError(null);
    const result = await putBio(value.trim());
    if (result.ok) {
      revalidator.revalidate(); // re-run meLoader → re-reads the saved bio
      onDone();
      return;
    }
    setSaving(false);
    setError(
      result.reason === 'invalid'
        ? 'That bio could not be saved — it must be 280 characters or fewer.'
        : 'Something went wrong saving your bio. Please try again.',
    );
  }

  return (
    <div className="tb-bio-edit">
      <label htmlFor="bio-input" className="tb-profile__note" style={{ marginTop: 0 }}>
        Bio
      </label>
      <textarea
        id="bio-input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={BIO_MAX + 20 /* soft slack; the count + server enforce the real cap */}
        disabled={saving}
      />
      <div className="tb-bio-edit__row">
        <button
          type="button"
          className="tb-btn tb-btn--primary"
          onClick={() => void save()}
          disabled={over || saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="tb-btn" onClick={onDone} disabled={saving}>
          Cancel
        </button>
        <span className={`tb-bio-edit__count${over ? ' tb-bio-edit__count--over' : ''}`}>
          {value.trim().length}/{BIO_MAX}
        </span>
      </div>
      {error && <p className="tb-bio-edit__error">{error}</p>}
    </div>
  );
}

/** The two lists this section can show. `saved` = private bookmarks (FB-F02/F03), `adopted` = tools
    the owner has put into use. Both are owner-private ProfileToolSummary lists (see the type). */
type CollectionTab = 'saved' | 'adopted';

/**
 * "My Tool Collection" — the owner's saved + adopted tools under ONE header, replacing the old
 * two-column "Saved by me" / "Adopted by me" click-to-reveal boxes (Figma node 30:1804). A single
 * Saved / Adopted toggle on the right switches a bento grid of tool tiles; the two lists no longer
 * sit side by side, and the click-to-reveal gate is gone — this is already the owner's own private
 * view, so the tiles show by default (still owner-only, PR-F06; the "only you" framing is unchanged).
 *
 * DELIBERATE FIGMA DIVERGENCE — do not "fix" this back. `Profile Page Full V2.svg` draws this section
 * as a grid of the measured 191x170 ToolCard tile, and the Figma file has NOT yet been updated. The
 * tiles here are BentoGridItems instead (design/BentoGrid.tsx), chosen because a collection is a
 * heterogeneous browse surface: each tile carries publication status and health, which the measured
 * tile's two fixed text lines could only express by concatenating into its byline. ToolCard is NOT
 * dead — it remains the right tile for uniform, comparison-oriented surfaces (a "most popular tools"
 * strip, catalogue and search grids), where its fixed geometry is the point. Both components are
 * live, on the surfaces each suits. Tell the Figma owner so the file catches up.
 *
 * The grid keeps the config's `grid-cols-cards` auto-fill template (repeat(auto-fill, minmax(191px,
 * 1fr))) via BentoGrid's own default, so tiles flow from one column (< 645px, where --card-min-width
 * becomes 100%) up to as many as fit — no dead `md:` breakpoint. Each tile's visual block is sized to
 * the measured 117px thumbnail height, so a bento tile and a ToolCard tile remain the same shape.
 *
 * Each tile links to /tools/:id; the whole card is the link. The staggerContainer variants play
 * BentoGridItem's riseInItem entrance across the tiles; keying the grid on the active tab replays it
 * when the toggle switches.
 */
function MyToolCollection({
  saved,
  adopted,
}: {
  saved: ProfileToolSummary[];
  adopted: ProfileToolSummary[];
}) {
  const [tab, setTab] = useState<CollectionTab>('saved');
  const tools = tab === 'saved' ? saved : adopted;
  const emptyText =
    tab === 'saved' ? "You haven't saved any tools yet." : "You haven't adopted any tools yet.";

  return (
    <section className="pp-section" aria-labelledby="collection-heading">
      <div className="pp-section-head">
        <h2 id="collection-heading">My Tool Collection</h2>
        {/* Saved / Adopted toggle, pinned right like the "Your Uploads" Publish chip. Two buttons in
            a tablist: the active one takes the accent fill, the other a quiet ink label. aria-selected
            + the roving controls make it a real tablist for assistive tech. */}
        <div
          className="pp-collection-toggle"
          role="tablist"
          aria-label="Show saved or adopted tools"
        >
          <button
            type="button"
            role="tab"
            id="pp-collection-tab-saved"
            aria-selected={tab === 'saved'}
            aria-controls="pp-collection-grid"
            className="pp-collection-toggle__btn"
            onClick={() => setTab('saved')}
          >
            Saved
          </button>
          <button
            type="button"
            role="tab"
            id="pp-collection-tab-adopted"
            aria-selected={tab === 'adopted'}
            aria-controls="pp-collection-grid"
            className="pp-collection-toggle__btn"
            onClick={() => setTab('adopted')}
          >
            Adopted
          </button>
        </div>
      </div>

      {tools.length === 0 ? (
        <div className="pp-collection-panel pp-collection-panel--empty">
          <p className="tb-profile__unset" style={{ margin: 0 }}>
            {emptyText}
          </p>
        </div>
      ) : (
        /* THE BENTO GRID, and staging's self-declared `<dl>` is NOT lost here. Git aligned this
            slot against `SelfDeclaredSection`'s body — the two functions are unrelated; they merely
            occupied the same position in the file. That section was removed on this branch on purpose
            and its content re-homed into `AvatarPanel` read-only (see the "belonged to the removed
            'About you' section" comment there): bio via BioEditor, all three Pills, competency, maker
            type, the self-declared note, and Certifications with years. Every row staging drew has a
            home, so UR-F04 and the self-declared disclosure both still hold. */
        <motion.div
          // Keyed on the tab so switching remounts the grid and replays the stagger.
          key={tab}
          id="pp-collection-grid"
          role="tabpanel"
          aria-labelledby={`pp-collection-tab-${tab}`}
          variants={staggerContainer()}
          initial="hidden"
          animate="visible"
          className="pp-collection-panel"
        >
          <BentoGrid>
            {tools.map((t) => (
              <Link key={t.toolId} to={`/tools/${t.toolId}`} className="pp-collection-tile">
                <BentoGridItem
                  header={<ToolCollectionThumb tool={t} />}
                  title={t.name}
                  description={<ToolCollectionMeta tool={t} />}
                />
              </Link>
            ))}
          </BentoGrid>
        </motion.div>
      )}
    </section>
  );
}

/**
 * One tool's generated mark, on this page's two tile surfaces.
 *
 * EITHER TIER, decided by the data and not by the slot. `flowShape` is the encoded topology the backend
 * now sends on every ProfileToolSummary; when it is a readable code FlowArt draws the real diagram, and
 * when it is null it draws an identity emblem — arcs, a monogram, connector ticks, no nodes and no
 * edges. Passing it straight through is what keeps those two honest: a tool whose definition we could
 * not walk looks visibly different from one we could, rather than getting a plausible diagram a viewer
 * has no way to check. That is the rule packages/api/src/dataverse/components.ts sets for an unreadable
 * definition, and null is a normal answer here rather than a failure.
 *
 * The string is handed over OPAQUE. It is decoded inside FlowArt and nothing on this page reads it,
 * because the codec's version prefix is the only compatibility contract and a second reader is a second
 * place to break it. It carries no author text by construction, so there is nothing here to escape.
 *
 * `known` is still not passed: this payload has no connector count of its own, so an emblem draws its
 * ticks in the dashed UNKNOWN register rather than as counted pads. A diagram does not need it — its
 * connector families come from the shape itself. A guess must never look like a measurement.
 *
 * NO `label`, so the svg is `aria-hidden` — both call sites already render the name and status in text
 * beside it, and the mark carries nothing a reader could not get from that text.
 *
 * `fa--bare` because both boxes bring their own tint; see the class's own note in flowArt.css.
 */
function ToolArt({
  name,
  shape,
  width,
  height,
}: {
  name: string;
  shape: string | null;
  width: number;
  height: number;
}) {
  return <FlowArt name={name} shape={shape} width={width} height={height} className="fa--bare" />;
}

/**
 * The tile's visual block: the measured 117px box, now carrying the tool's generated mark instead of
 * standing empty. The box is untouched — same height token, same mint tint, same radius — and the art
 * is a CHILD of it rather than a replacement for it, so a real thumbnail field arriving later still has
 * its slot.
 *
 * WIDTH is the one number that had to be derived rather than read: the bento track is
 * `minmax(--card-min-width, 1fr)` = `minmax(191px, 1fr)` and BentoGridItem insets by `p-card`
 * (--card-inset: 12px) each side, so the box is 167px wide at the narrowest track and wider on a wide
 * one. 167 is therefore the NOMINAL viewBox width; on a wider tile `preserveAspectRatio="meet"` centres
 * the mark and the spare width shows the tile's own mint, which is why this slot takes `fa--bare`.
 *
 * The health DOT is still the one thing drawn OVER it, and only for the two states that change what the
 * tool is worth to the reader — Broken and Unmaintained. A Healthy tool draws nothing, matching the
 * search dropdown's healthNote() (DS-F20/DS-F13). `title` carries the word itself so the colour is
 * never the only channel; the dot is aria-hidden because ToolCollectionMeta already states it in text.
 * It stays a LATER SIBLING of the art with no z-index on either: both are positioned boxes at
 * `z-index: auto`, so DOM order alone paints the dot on top, and it keeps its own hit area for `title`.
 */
function ToolCollectionThumb({ tool }: { tool: ProfileToolSummary }) {
  const healthState = tool.healthState;
  const flagged = healthState === 'Broken' || healthState === 'Unmaintained';
  return (
    <div className="relative flex h-tool-card-thumb w-full items-center justify-center overflow-hidden rounded-md bg-accent-subtle">
      <ToolArt name={tool.name} shape={tool.flowShape} width={167} height={117} />
      {flagged && (
        <span
          aria-hidden="true"
          title={healthState}
          className={`absolute right-2 top-2 h-2 w-2 rounded-full ${
            healthState === 'Broken' ? 'bg-error' : 'bg-icon'
          }`}
        />
      )}
    </div>
  );
}

/**
 * The tile's sub-line. Publication status always; health appended ONLY when it is Broken or
 * Unmaintained, per the same DS-F20/DS-F13 rule as the dot above — a Healthy tool reads just
 * "Approved", not "Approved · Healthy". This is the text channel for the dot, so the state is never
 * conveyed by colour alone.
 *
 * `leaderboard` is deliberately not rendered: the adopted/saved lists never carry it (only GET
 * /api/me/tools does), so it would always be null here. `withdrawn` is likewise always false on
 * these two lists — both are documented on ProfileToolSummary.
 */
function ToolCollectionMeta({ tool }: { tool: ProfileToolSummary }) {
  const flagged = tool.healthState === 'Broken' || tool.healthState === 'Unmaintained';
  return (
    <>{flagged ? `${tool.publicationStatus} · ${tool.healthState}` : tool.publicationStatus}</>
  );
}

/**
 * "Tools you've shared" — the owner's submission tracker (PR-F34), and the entry point to Will's
 * ProfileFlow Tool Overlay. Same private/owner-only framing as the adopted/saved columns (it is a
 * shared-list variant), but each row is a BUTTON that opens the overlay for that tool instead of a
 * plain link, so the owner can act on their own submission (see rejection reason, edit, withdraw/
 * restore). The adopted/saved lists stay on AdoptedSavedColumns — those are not the owner's own
 * submissions.
 *
 * On any overlay action that changes a tool (edit that re-queues, withdraw, restore), the overlay
 * revalidates the /me loader; this list re-reads so the row's status/label updates in place.
 */
/**
 * The three stops on a tool's route to the catalogue, and where a given publication status sits on it.
 *
 * The middle stop is WORDED rather than statused. "Pending" is the database's word for it and tells the
 * owner nothing about who is holding their tool; this is the same phrasing the upload wizard's completion
 * screen uses ("It is now with the AI SWAT team…"), so the two surfaces do not describe one state two
 * ways.
 */
const PUBLICATION_STOPS = ['Draft', 'With the AI SWAT team', 'Approved'] as const;

/**
 * Which stop a status is at, or null for a status that is not ON this road.
 *
 * `Rejected` returns null on purpose: it is a departure from the route, not a place along it, and the
 * overlay already carries a callout explaining it. Anything unrecognised returns null too — a status
 * added server-side later should make the journey disappear rather than have it guess a position and
 * quietly tell the owner something untrue.
 */
function publicationJourneyStop(status: string): { at: number; complete: boolean } | null {
  if (status === 'Draft') return { at: 0, complete: false };
  if (status === 'Pending') return { at: 1, complete: false };
  if (status === 'Approved') return { at: 2, complete: true };
  return null;
}

function PublicationJourney({ status }: { status: string }) {
  const stop = publicationJourneyStop(status);
  if (!stop) return null;
  return (
    <div style={{ margin: '0.6rem 0 0.9rem', maxWidth: '30rem' }}>
      <KombiJourney
        stops={PUBLICATION_STOPS}
        current={stop.at}
        complete={stop.complete}
        label="Publication progress"
        vanHeight={34}
      />
    </div>
  );
}

/** One-line status text for a tool's gallery panel: publication status, health, and any Top-5 note. */
function submissionStatusLine(t: ProfileToolSummary): string {
  const parts: string[] = [];
  if (t.withdrawn) parts.push('Withdrawn from catalogue');
  let core = t.publicationStatus;
  if (t.healthState !== 'Healthy') core += `, ${t.healthState}`;
  parts.push(core);
  // RT-F20: the builder's OWN tool's live Top-5 standing, shown IN TEXT (movementText is
  // authoritative, RT-NFR04). Present only when the tool is currently in the Top 5.
  if (t.leaderboard) {
    parts.push(
      `#${t.leaderboard.position} in the weekly Top 5 (${movementText(
        t.leaderboard.movement,
        t.leaderboard.movementPlaces,
      )})`,
    );
  }
  return parts.join(' · ');
}

function SubmissionsSection({ tools }: { tools: ProfileToolSummary[] }) {
  const [openToolId, setOpenToolId] = useState<string | null>(null);
  // The element that opened the overlay, so focus can return to it on close (accessibility). The
  // gallery panels are the triggers now; we capture the focused element at open time.
  const openerRef = useRef<HTMLElement | null>(null);

  // WITHDRAWN SUBMISSIONS SINK TO THE END of the row, ahead of the terminal "all your tools" tile.
  // A withdrawn tool is not in the catalogue, so it is the least likely thing the owner came here to
  // act on; leading with it would put a dead entry in the expanded slot on first paint (the gallery
  // opens on index 0). Live submissions first keeps the default expansion useful.
  //
  // STABLE, and deliberately so: a comparator that returns 0 for same-group pairs preserves the
  // backend's own ordering inside each group, so live tools keep the /api/me/tools order (most recent
  // first) instead of being re-shuffled into an arbitrary one. Array#sort has been required to be
  // stable since ES2019, so the 0 case is dependable rather than incidental.
  //
  // COPY FIRST, then sort. Two reasons, and the copy is not incidental:
  //  - `tools` is loader-owned data; sorting in place would mutate the array the router holds and
  //    could reorder it under an unrelated re-render.
  //  - it is `[...tools].sort()` rather than the tidier `tools.toSorted()` because vite.config.ts
  //    builds to `target: 'es2022'` and toSorted is ES2023. Vite's target downlevels SYNTAX, not
  //    library methods, so toSorted would typecheck (tsconfig lists lib ES2023), build clean, and then
  //    throw "tools.toSorted is not a function" at runtime on anything below Chrome 110 / Safari 16.
  //    Nothing else in this repo uses the ES2023 array methods, so there is no precedent saying the
  //    floor has moved. Raise the build target first if you want them.
  const ordered = [...tools].sort(
    (a, b) => Number(a.withdrawn ?? false) - Number(b.withdrawn ?? false),
  );

  // ProfileToolSummary still carries no image (a real thumbnail field is coming from the backend), so
  // each panel takes the tool's GENERATED mark instead of the gallery's flat placeholder fill — the real
  // diagram where `flowShape` decoded, an identity emblem where it did not. It goes in
  // `icon` rather than `image` because `image` is a URL and this is a node; AccordionGallery already
  // prefers `icon` over both `image` and the placeholder (see its media branch), so that component needs
  // no change. `image` is dropped rather than left as null — with `icon` set it could never be reached,
  // and a dead field reads as a pending one.
  //
  // 360x270 is the ACTIVE panel: `--ag-w-active` by `.accordion-gallery`'s own 270px height. A collapsed
  // spine is `--ag-w-spine` (90px), where `meet` shrinks the mark to 90x67.5 and centres it in the mint
  // — the mark gets smaller with the panel instead of being cropped, which is the whole reason the
  // viewBox is `meet` and not `slice`.
  const toolItems: AccordionGalleryItem[] = ordered.map((t) => ({
    id: t.toolId,
    label: t.name,
    sublabel: submissionStatusLine(t),
    icon: <ToolArt name={t.name} shape={t.flowShape} width={360} height={270} />,
    variant: 'tool' as const,
    // A padlock top-right when the owner has withdrawn this submission from the catalogue. Reads the
    // dedicated `withdrawn` boolean, NOT publicationStatus — the two are independent axes (the tool
    // keeps its status while withdrawn; see ProfileToolSummary and vocabulary.ts's lifecycle note), so
    // string-matching 'Withdrawn' would be wrong. Purely a redundant cue: submissionStatusLine already
    // puts "Withdrawn from catalogue" in the sublabel, which is also the panel's accessible name, so
    // the state is never conveyed by icon (or colour) alone.
    //
    // The padlock stays visible at SPINE width too, unlike the text label which fades out — see
    // `.ag-panel__badge` in accordionGallery.css. Withdrawn tools now sort to the end of the row, so
    // they are usually the collapsed spines: a marker that vanished exactly when the panel collapsed
    // would hide the state on precisely the panels carrying it.
    badge: t.withdrawn ? <PadlockIcon /> : null,
  }));

  /**
   * The TERMINAL "view all" tile, appended after the tool panels. It stands for the whole set rather
   * than one tool, so it takes the gallery's `summary` variant (solid --accent-strong, no media
   * placeholder) to read as a different KIND of thing than the mint tool spines beside it.
   *
   * It carries an `href` rather than an id the overlay could open: the catalogue already answers
   * "all the tools I uploaded" via ?scope=mine (routes/tools.ts catalogueQuery), so this navigates
   * there instead of adding a route. Because it has an href the gallery renders it as a real <a>,
   * which is what makes it keyboard- and middle-click-navigable for free.
   *
   * Its id is prefixed `__` so it can never collide with a real toolId, and SubmissionsSection's
   * onActivate ignores it — clicking it navigates, it does not open the per-tool overlay.
   */
  const SUMMARY_ID = '__view-all';
  const items: AccordionGalleryItem[] = [
    ...toolItems,
    {
      id: SUMMARY_ID,
      label: 'All your tools',
      sublabel: `View all ${tools.length} ${tools.length === 1 ? 'upload' : 'uploads'}`,
      image: null,
      href: '/tools?scope=mine',
      variant: 'summary' as const,
      // A library glyph rather than a thumbnail: this tile is a fixed-width end-cap that never
      // expands, so the icon is what people recognise it by. Its visible text label is hidden at row
      // width (see .ag-panel--summary .ag-panel__label) but the label+sublabel above still form the
      // panel's aria-label, so the destination is fully announced.
      icon: <LibraryIcon />,
    },
  ];

  // THE ACCORDION REPLACES staging's <ul> of rows, and the three things that list carried are
  // each accounted for rather than dropped:
  //   · RT-F20's Top-5 standing — folded into every panel's sublabel by `submissionStatusLine`
  //     above, still as authoritative TEXT (RT-NFR04), so it survives at accordion width.
  //   · the "Withdrawn from catalogue" tag — likewise in the sublabel, plus a padlock badge that
  //     stays visible at spine width (see accordionGallery.css's .ag-panel__badge note).
  //   · the "Private — only you can see this" marker and the heading — moved OUT to the "Your
  //     Uploads" wrapper in Profile() (Figma Frame 66), which is what the comment below refers to.
  // PR-F38's ToolActivity figures are the one thing NOT on the row any more: a 90px spine has
  // nowhere to put four figures. They are still one click away, unchanged, in SubmissionOverlay
  // below — the same component the old row rendered them through.
  return (
    // No heading/box chrome here — the "Your Uploads" section wrapper in Profile() supplies the
    // heading, the Publish action and the bordered container. This renders just the gallery (or the
    // empty state) plus the per-tool overlay, so it drops cleanly into that container. The frame shows
    // the carousel alone inside the box — no privacy marker or descriptive blurb.
    <>
      {tools.length === 0 ? (
        <p className="pp-uploads-empty">Make your first upload!</p>
      ) : (
        <AccordionGallery
          items={items}
          grayscale={false}
          bentoLabel
          onActivate={(item) => {
            // The terminal "view all" tile is a real <a> — let the navigation happen and never try to
            // open a per-tool overlay for an id that is not a tool.
            if (item.id === SUMMARY_ID) return;
            // Remember the panel that opened the overlay so we can restore focus to it on close.
            openerRef.current = (document.activeElement as HTMLElement) ?? null;
            setOpenToolId(item.id);
          }}
        />
      )}
      {openToolId && (
        <SubmissionOverlay
          toolId={openToolId}
          onClose={() => {
            const opener = openerRef.current;
            setOpenToolId(null);
            // Return focus to the panel that opened it (accessibility).
            opener?.focus();
          }}
        />
      )}
    </>
  );
}

/**
 * Will's ProfileFlow "Tool Overlay" — what opens when the owner clicks one of their submitted tools.
 * Fetches the tool's editable detail on open (GET /api/me/tools/:toolId) and offers the owner actions
 * confirmed for the POC:
 *
 *   - Rejected → shows the reviewer's reason prominently, then the edit form to fix & resubmit.
 *   - Edit description (description / "what problem it solves") — a FREE edit: saving it leaves the
 *     tool's status untouched (no AI SWAT re-review).
 *   - Edit instructions / connectors (setup, dependencies, connector notes — the "step-by-step what
 *     they do" section) — saving a change here sends the tool BACK to the AI SWAT team (→ Pending),
 *     which also removes it from the public catalogue until re-approved. The form warns before saving.
 *   - Withdraw / Restore — with a confirm step (SVG). Withdraw hides the tool from the catalogue;
 *     Restore brings it back to whatever status it holds.
 *   - Exit.
 *
 * Upload-a-new-version is OUT of POC scope (red box in the flow) and is deliberately absent.
 *
 * TRUST: talks only to /api/me/* with the session cookie; no user id/token is ever sent, and the tool
 * body never carries clientdata. On a mutating success it revalidates the /me loader so the shared
 * list behind it re-reads.
 *
 * ACCESSIBILITY: role="dialog" + aria-modal; Escape and backdrop-click close; focus returns to the
 * trigger on close (handled by the caller's onClose).
 */
// ── Boxed per-step setup editor (mirrors upload/InstructionsStage) ─────────────────────────────
// The structured 0020 docs are the source of truth for setup instructions. The step scaffolding
// (number, branch, label, type/connector meta) is READ-ONLY here — it comes from the captured flow,
// and letting the owner rewrite it would desync the backend. Only each step's explanation is editable,
// exactly as the upload flow presents it, so a builder editing from their profile sees the same shape.

/** Read-only context line: type · connector · operation, as far as each is known (upload parity). */
function stepMeta(step: FlowComponentDoc): string {
  const parts: string[] = [];
  if (step.typeLabel) parts.push(step.typeLabel);
  if (step.connector) parts.push(step.connector);
  if (step.operation) parts.push(step.operation);
  return parts.join(' · ');
}

/** The prompt under each step, tuned to what the component is — the same asks the upload flow uses. */
function stepPrompt(step: FlowComponentDoc): string {
  if (step.kind === 'trigger') {
    return 'What starts this flow, and what must be in place first? Name the form, list, mailbox or schedule it watches.';
  }
  if (step.actionType === 'If') {
    return 'What is this condition checking, and what decides yes or no? Say which value an adopter may need to change.';
  }
  if (step.actionType === 'Foreach' || step.actionType === 'Until') {
    return 'What does this loop run over, and how many items should an adopter expect?';
  }
  if (step.actionType === 'Switch') {
    return 'What value is being matched here, and what should an adopter change for their own cases?';
  }
  if (step.kind === 'control') {
    return 'What is this group of steps for, and does anything about it need configuring?';
  }
  return 'What does this step do, what needs configuring, and which connection or account is required?';
}

/**
 * The list of step cards. `steps` is the ordered structured docs; `explanations` is the live edit
 * state keyed by step id; `onChange` writes one step's explanation. Numbering is derived on the fly
 * with the shared `numberComponents` (docs store no numbering — it is a function of current structure).
 * `flagInvalid` highlights only the ids the server rejected on the last save attempt.
 */
function StepCards({
  steps,
  explanations,
  onChange,
  flagInvalid,
}: {
  steps: FlowComponentDoc[];
  explanations: Record<string, string>;
  onChange: (id: string, value: string) => void;
  flagInvalid: (id: string) => boolean;
}) {
  // Derive display numbering (1, 3.1, 3.2.1) from the ordered docs — mirrors ReviewStage/upload.
  const numbered = numberComponents(steps.map((d) => ({ ...d, numbering: '' })) as FlowComponent[]);
  return (
    <ul className="tb-comp-list">
      {steps.map((step, i) => {
        const value = explanations[step.id] ?? '';
        const isMissing = !isExplained(value);
        const flagged = flagInvalid(step.id);
        const meta = stepMeta(step);
        const numbering = numbered[i]?.numbering ?? String(i + 1);
        const errorId = `tb-step-err-${step.order}`;
        return (
          <li
            key={step.id}
            className="tb-comp"
            data-depth={Math.min(step.depth, 5)}
            data-deep={step.depth > 5 ? 'true' : 'false'}
            data-missing={isMissing ? 'true' : 'false'}
          >
            {/* Read-only header — NOT a button, NOT editable. The step's identity comes from the flow. */}
            <div className="tb-comp-head tb-comp-head--static">
              <span className="tb-comp-num">{numbering}</span>
              {step.branch ? <span className="tb-comp-branch">{step.branch}</span> : null}
              <span className="tb-comp-label">{step.label}</span>
              <span className="tb-comp-state" data-missing={isMissing ? 'true' : 'false'}>
                {isMissing ? '● Needs explaining' : '✓ Explained'}
              </span>
              {meta ? <span className="tb-comp-meta">{meta}</span> : null}
            </div>
            <div className="tb-comp-body">
              <label className="tb-field">
                <span className="tb-field-hint" style={{ fontWeight: 400 }}>
                  {stepPrompt(step)}
                </span>
                <textarea
                  className="tb-field__input"
                  value={value}
                  maxLength={MAX_EXPLANATION_LENGTH}
                  onChange={(e) => onChange(step.id, e.target.value)}
                  aria-invalid={flagged ? true : undefined}
                  aria-describedby={flagged ? errorId : undefined}
                  aria-label={`How to set up step ${numbering}, ${step.label}`}
                />
                {flagged ? (
                  <span className="tb-err" id={errorId}>
                    Explain this step before saving.
                  </span>
                ) : null}
              </label>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function SubmissionOverlay({ toolId, onClose }: { toolId: string; onClose: () => void }) {
  const revalidator = useRevalidator();
  const [detail, setDetail] = useState<MySubmissionDetail | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // Edit-form field state (populated once detail loads).
  const [description, setDescription] = useState('');
  const [problemSolved, setProblemSolved] = useState('');
  const [setupInstructions, setSetupInstructions] = useState('');
  const [dependencies, setDependencies] = useState('');
  const [connectorSetupNotes, setConnectorSetupNotes] = useState('');
  // Per-step explanation edit state, keyed by component id — seeded from the loaded structured docs.
  // Empty/absent when the tool is legacy (no docs), in which case the flat textarea is shown instead.
  const [stepExplanations, setStepExplanations] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  // Confirm step for withdraw/restore ('withdraw' | 'restore' | null).
  const [confirming, setConfirming] = useState<'withdraw' | 'restore' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Load the tool's detail on open (and whenever the id changes).
  useEffect(() => {
    let live = true;
    setDetail(null);
    setLoadFailed(false);
    void fetchMySubmission(toolId).then((d) => {
      if (!live) return;
      if (!d) {
        setLoadFailed(true);
        return;
      }
      setDetail(d);
      setDescription(d.description);
      setProblemSolved(d.problemSolved);
      setSetupInstructions(d.setupInstructions);
      setDependencies(d.dependencies);
      setConnectorSetupNotes(d.connectorSetupNotes ?? '');
      // Seed the per-step edit state from the structured docs (id → explanation). Legacy tools with no
      // docs get an empty map and fall back to the flat setup-instructions textarea below.
      setStepExplanations(
        d.steps ? Object.fromEntries(d.steps.map((s) => [s.id, s.explanation])) : {},
      );
    });
    return () => {
      live = false;
    };
  }, [toolId]);

  // Move focus into the panel on open.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // A tool with structured 0020 docs edits its steps through the boxed cards; a legacy tool edits the
  // flat setup-instructions textarea. Exactly one of these paths is active per tool.
  const hasStructuredSteps = detail !== null && detail.steps !== null && detail.steps.length > 0;

  // Which step explanations differ from what loaded (compared against the trimmed stored value, matching
  // the backend, which trims before comparing). Only these ids are sent on save.
  const changedStepIds =
    detail?.steps
      ?.filter((s) => (stepExplanations[s.id] ?? '') !== s.explanation)
      .map((s) => s.id) ?? [];
  const stepsChanged = changedStepIds.length > 0;

  // Has the owner changed an INSTRUCTIONS-group field? For a structured tool, editing a step counts;
  // the flat setupInstructions is only in play for a legacy tool.
  const instructionsChanged =
    detail !== null &&
    (stepsChanged ||
      (!hasStructuredSteps && setupInstructions !== detail.setupInstructions) ||
      dependencies !== detail.dependencies ||
      connectorSetupNotes !== (detail.connectorSetupNotes ?? ''));
  const descriptionChanged =
    detail !== null &&
    (description !== detail.description || problemSolved !== detail.problemSolved);
  const anyChange = instructionsChanged || descriptionChanged;

  // Will THIS edit actually re-queue for SWAT? Only when an instructions-group change lands on a tool
  // that was ever on the SWAT track (Will's refinement) — mirrors the server gate so the warning and
  // button copy are honest. A self-vouched Draft (never approved, still Draft) edits its steps freely.
  const wasOnSwatTrack =
    detail !== null && (detail.wasApproved || detail.publicationStatus !== 'Draft');
  const willRequeue = instructionsChanged && wasOnSwatTrack;

  async function save() {
    if (!detail || saving || !anyChange) return;
    setSaving(true);
    setError(null);
    setInvalidFields([]);
    // Send only the fields that actually changed, so a description-only edit never touches
    // instructions (and vice versa) — this is what keeps the backend's group detection honest.
    const patch: EditSubmissionPatch = {};
    if (description !== detail.description) patch.description = description;
    if (problemSolved !== detail.problemSolved) patch.problemSolved = problemSolved;
    // Structured tool: send only the changed step explanations (the backend re-derives the flat text).
    // Legacy tool: send the flat setup-instructions textarea as before.
    if (hasStructuredSteps) {
      if (changedStepIds.length > 0) {
        patch.stepExplanations = Object.fromEntries(
          changedStepIds.map((id) => [id, stepExplanations[id] ?? '']),
        );
      }
    } else if (setupInstructions !== detail.setupInstructions) {
      patch.setupInstructions = setupInstructions;
    }
    if (dependencies !== detail.dependencies) patch.dependencies = dependencies;
    if (connectorSetupNotes !== (detail.connectorSetupNotes ?? ''))
      patch.connectorSetupNotes =
        connectorSetupNotes.trim().length === 0 ? null : connectorSetupNotes;

    const result = await patchMySubmission(toolId, patch);
    if (result.ok) {
      revalidator.revalidate(); // re-run meLoader → the shared list re-reads the new status/label
      if (result.requeued) {
        setNotice(
          "Saved. Because you changed the setup steps, this tool has gone back to the AI SWAT team for review — it's hidden from the catalogue until they approve it again.",
        );
      } else {
        setNotice('Saved. Your changes are live.');
      }
      // Reflect the new baseline so further edits diff against the saved values. For a structured tool,
      // fold the edited explanations back into the stored docs so the cards' saved state is accurate.
      const nextSteps =
        detail.steps && hasStructuredSteps
          ? detail.steps.map((s) => ({
              ...s,
              explanation: stepExplanations[s.id] ?? s.explanation,
            }))
          : detail.steps;
      setDetail({
        ...detail,
        description,
        problemSolved,
        setupInstructions,
        dependencies,
        connectorSetupNotes: connectorSetupNotes.trim().length === 0 ? null : connectorSetupNotes,
        publicationStatus: result.publicationStatus,
        steps: nextSteps,
      });
      setSaving(false);
      return;
    }
    setSaving(false);
    if (result.reason === 'invalid') {
      setInvalidFields(result.detail);
      setError('Some fields need attention before this can be saved.');
    } else if (result.reason === 'not_found') {
      setError('This tool could not be found. It may have been removed.');
    } else {
      setError('Something went wrong saving your changes. Please try again.');
    }
  }

  async function doWithdrawRestore(withdrawn: boolean) {
    if (saving) return;
    setSaving(true);
    setError(null);
    const result = await setMySubmissionWithdrawn(toolId, withdrawn);
    setSaving(false);
    setConfirming(null);
    if (result.ok) {
      revalidator.revalidate();
      if (detail) setDetail({ ...detail, withdrawn });
      setNotice(
        withdrawn
          ? 'Withdrawn. This tool is now hidden from the catalogue. You can restore it any time.'
          : 'Restored. This tool is back in the catalogue at its current status.',
      );
      return;
    }
    setError(
      withdrawn
        ? 'Something went wrong withdrawing this tool. Please try again.'
        : 'Something went wrong restoring this tool. Please try again.',
    );
  }

  const invalid = (f: string) => invalidFields.includes(f);

  return (
    <div
      className="tb-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        ref={panelRef}
        className="tb-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="submission-overlay-heading"
      >
        <h2 id="submission-overlay-heading" ref={headingRef} tabIndex={-1}>
          {detail ? detail.name : 'Your tool'}
          {detail?.withdrawn && (
            <span className="tb-withdrawn-tag" title="This tool is hidden from the catalogue">
              Withdrawn from catalogue
            </span>
          )}
        </h2>

        {loadFailed && (
          <p className="tb-profile__unset">
            This tool&apos;s details couldn&apos;t be loaded. Please close this and try again.
          </p>
        )}

        {!detail && !loadFailed && <p className="tb-profile__note">Loading…</p>}

        {detail && (
          <>
            <p className="tb-profile__note" style={{ marginTop: 0 }}>
              Status: <strong>{detail.publicationStatus}</strong>
              {detail.withdrawn ? ' · hidden from the catalogue until you restore it' : ''}
            </p>

            {/* THE PUBLICATION JOURNEY. The line above names the status; this shows the owner WHERE that
                status sits on the route their tool is travelling, which the word alone does not. It is
                the only one of the Kombi journeys driven by real server state rather than by a UI
                position — a tool sits at "With the AI SWAT team" for as long as review actually takes.

                The middle stop is worded, not statused: "Pending" is the database's word and it says
                nothing about who is holding the tool or why. "With the AI SWAT team" is the same wording
                the upload wizard's completion screen already uses, so the two surfaces agree.

                RENDERS FOR THREE STATUSES ONLY. `Rejected` deliberately shows nothing — it is not a stop
                on this road but a departure from it, and the warn callout a few lines below already says
                what happened and what to do. Anything else unrecognised (a status added later) also
                renders nothing rather than guessing a position: see publicationJourneyStop. */}
            <PublicationJourney status={detail.publicationStatus} />

            {/* PR-F38: the recognition view — the SAME four figures, through the SAME component, as the
                list row that opened this overlay. Counts only: the server sends no identities with them,
                so this cannot say who adopted, liked or commented (PR-F38's closing clause, PR-F28). */}
            <ToolActivity activity={detail.activity} />

            {/* Rejected: the reviewer's reason, so the owner knows what to fix (SVG "reviewers reason"). */}
            {detail.publicationStatus === 'Rejected' && (
              <div className="tb-callout tb-callout--warn" role="note">
                <strong>The AI SWAT team asked for changes.</strong>
                <p style={{ margin: '0.4rem 0 0' }}>
                  {detail.rejectionReason && detail.rejectionReason.trim().length > 0
                    ? detail.rejectionReason
                    : 'No specific reason was recorded. Review your setup steps and resubmit.'}
                </p>
                <p className="tb-profile__note" style={{ margin: '0.4rem 0 0' }}>
                  Editing the setup steps below will send the tool back to them for another review.
                </p>
              </div>
            )}

            {notice && (
              <div className="tb-callout" role="status">
                {notice}
              </div>
            )}
            {error && (
              <div className="tb-callout tb-callout--error" role="alert">
                {error}
              </div>
            )}

            {/* ── Description group — FREE edit, no re-review ── */}
            <fieldset className="tb-fieldset">
              <legend>About this tool</legend>
              <p className="tb-profile__note" style={{ marginTop: 0 }}>
                You can change these any time — they don&apos;t need another review.
              </p>
              <label className="tb-field">
                <span className="tb-field__label">Description</span>
                <textarea
                  className="tb-field__input"
                  value={description}
                  rows={3}
                  aria-invalid={invalid('description') || undefined}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </label>
              <label className="tb-field">
                <span className="tb-field__label">What problem it solves</span>
                <textarea
                  className="tb-field__input"
                  value={problemSolved}
                  rows={3}
                  aria-invalid={invalid('problemSolved') || undefined}
                  onChange={(e) => setProblemSolved(e.target.value)}
                />
              </label>
            </fieldset>

            {/* ── Instructions / connectors group — on a SWAT-track tool, editing RE-QUEUES for review ── */}
            <fieldset className="tb-fieldset">
              <legend>Setup &amp; connectors</legend>
              {/* The re-review warning shows ONLY when this tool was ever on the SWAT track (Will's
                  refinement). A self-vouched Draft can refine its steps freely, so it gets reassuring
                  copy instead of a warning. The server is authoritative on the actual outcome. */}
              {wasOnSwatTrack ? (
                <p className="tb-callout tb-callout--warn" style={{ marginTop: 0 }}>
                  These explain step by step how the tool works. If you change anything here, the
                  tool goes back to the AI SWAT team for review and is hidden from the catalogue
                  until they approve it again.
                </p>
              ) : (
                <p className="tb-profile__note" style={{ marginTop: 0 }}>
                  These explain step by step how the tool works. This tool wasn&apos;t reviewed by
                  the AI SWAT team, so you can refine these explanations without sending it for
                  review.
                </p>
              )}

              {/* Setup steps. A structured tool (0020 docs) shows one read-only step card per flow
                  component with an editable explanation — the step's number, name and connector come
                  from the captured flow and can't be changed here. A legacy tool falls back to the
                  single free-text box it was created with. */}
              {hasStructuredSteps && detail.steps ? (
                <div className="tb-field">
                  <span className="tb-field__label">Setup instructions — explain each step</span>
                  <StepCards
                    steps={detail.steps}
                    explanations={stepExplanations}
                    onChange={(id, value) =>
                      setStepExplanations((prev) => ({ ...prev, [id]: value }))
                    }
                    flagInvalid={(id) => invalid(`step:${id}`)}
                  />
                </div>
              ) : (
                <label className="tb-field">
                  <span className="tb-field__label">Setup instructions</span>
                  <textarea
                    className="tb-field__input"
                    value={setupInstructions}
                    rows={4}
                    aria-invalid={invalid('setupInstructions') || undefined}
                    onChange={(e) => setSetupInstructions(e.target.value)}
                  />
                </label>
              )}
              <label className="tb-field">
                <span className="tb-field__label">Dependencies</span>
                <textarea
                  className="tb-field__input"
                  value={dependencies}
                  rows={2}
                  aria-invalid={invalid('dependencies') || undefined}
                  onChange={(e) => setDependencies(e.target.value)}
                />
              </label>
              <label className="tb-field">
                <span className="tb-field__label">Connector setup notes (optional)</span>
                <textarea
                  className="tb-field__input"
                  value={connectorSetupNotes}
                  rows={3}
                  aria-invalid={invalid('connectorSetupNotes') || undefined}
                  onChange={(e) => setConnectorSetupNotes(e.target.value)}
                />
              </label>
            </fieldset>

            {/* Live hint of the consequence, before they commit — only when the edit will actually
                re-queue (an instructions change on a SWAT-track tool). */}
            {willRequeue && (
              <p className="tb-profile__note" role="status">
                Saving will send this tool back to the AI SWAT team for review.
              </p>
            )}

            <div className="tb-modal__row">
              <button
                type="button"
                className="tb-btn tb-btn--primary"
                disabled={!anyChange || saving}
                onClick={() => void save()}
              >
                {saving ? 'Saving…' : willRequeue ? 'Save & send for review' : 'Save changes'}
              </button>
              <button type="button" className="tb-btn" onClick={onClose} disabled={saving}>
                Exit
              </button>
            </div>

            {/* ── Withdraw / Restore, with a confirm step (SVG) ── */}
            <div className="tb-modal__row" style={{ justifyContent: 'flex-start' }}>
              {confirming === null ? (
                detail.withdrawn ? (
                  <button
                    type="button"
                    className="tb-btn"
                    disabled={saving}
                    onClick={() => setConfirming('restore')}
                  >
                    Restore to catalogue
                  </button>
                ) : (
                  <button
                    type="button"
                    className="tb-btn tb-btn--danger"
                    disabled={saving}
                    onClick={() => setConfirming('withdraw')}
                  >
                    Withdraw from catalogue
                  </button>
                )
              ) : (
                <div className="tb-confirm" role="group" aria-label="Confirm">
                  <span>
                    {confirming === 'withdraw'
                      ? 'Withdraw this tool? It will be hidden from the catalogue until you restore it.'
                      : 'Restore this tool to the catalogue?'}
                  </span>
                  <button
                    type="button"
                    className={
                      confirming === 'withdraw' ? 'tb-btn tb-btn--danger' : 'tb-btn tb-btn--primary'
                    }
                    disabled={saving}
                    onClick={() => void doWithdrawRestore(confirming === 'withdraw')}
                  >
                    {saving
                      ? 'Working…'
                      : confirming === 'withdraw'
                        ? 'Yes, withdraw'
                        : 'Yes, restore'}
                  </button>
                  <button
                    type="button"
                    className="tb-btn"
                    disabled={saving}
                    onClick={() => setConfirming(null)}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The owner's private "Who I follow" list (PR-F61) — visible to this person alone. Each row is a
 * followed colleague: a small avatar (resolved through the shared allowlist, never a raw ref) + a
 * name linking to their public profile, and an Unfollow control that DELETEs the follow then
 * revalidates the /me loader so the row disappears. This is the ONLY browsable list of people in the
 * follow graph, and it is the OWNER'S OWN followees — there is deliberately no "who follows me" list
 * (PR-F52). The list is unranked (server orders by name); no per-row number, no count language here.
 *
 * The owner's own follower COUNT (PR-F52) is shown next to their name in the page header, not here —
 * this section is only the private "who I follow" list (PR-F61).
 */
/**
 * FollowingPanel — the "who I follow" list, opened from the "Following" stat in the band (per the
 * owner's design: the follow list is reachable by clicking the count next to your name, not shown as a
 * standing section). A modal dialog on the shared `tb-modal` shell, reusing the AvatarPanel/BadgePanel
 * conventions (backdrop + Escape close, heading focus on open, focus returned to the trigger on close).
 *
 * Owner-private and unchanged in data: the same FollowingEntry list with per-row unfollow, links to
 * each colleague's profile. There is NO "who follows me" list — that is a bare count only (PR-F52).
 */
function FollowingPanel({
  following,
  onClose,
  returnFocusRef,
}: {
  following: FollowingEntry[];
  onClose: () => void;
  returnFocusRef: React.RefObject<HTMLButtonElement>;
}) {
  const revalidator = useRevalidator();
  const [busyId, setBusyId] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Move focus into the panel on open; return it to the trigger on close (unmount).
  useEffect(() => {
    headingRef.current?.focus();
    const trigger = returnFocusRef.current;
    return () => trigger?.focus();
  }, [returnFocusRef]);

  async function unfollow(userId: string) {
    if (busyId) return;
    setBusyId(userId);
    const ok = await deleteFollow(userId);
    if (ok) {
      revalidator.revalidate(); // re-run meLoader → the row drops off and the list re-reads
    }
    setBusyId(null);
  }

  return (
    <div
      className="tb-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        className="tb-modal tb-modal--tall"
        role="dialog"
        aria-modal="true"
        aria-labelledby="following-panel-heading"
      >
        <h2 id="following-panel-heading" ref={headingRef} tabIndex={-1}>
          Following
        </h2>
        <div className="tb-modal__scroll">
          {following.length === 0 ? (
            <p className="tb-profile__unset" style={{ margin: 0 }}>
              You aren&apos;t following anyone yet. Open a colleague&apos;s profile to follow them.
            </p>
          ) : (
            <ul className="tb-follow-list">
              {following.map((f) => {
                const avatar = resolveAvatar(
                  { avatarKind: f.avatarKind, avatarBlobRef: f.avatarBlobRef },
                  f.photoBlobRef,
                );
                return (
                  <li key={f.userId} className="tb-follow-row">
                    {avatar ? (
                      // eslint-disable-next-line jsx-a11y/img-redundant-alt -- alt is a name, intentional
                      <img
                        className="tb-follow-row__avatar"
                        src={avatar}
                        alt={`${f.displayName}'s profile photo`}
                      />
                    ) : (
                      <span
                        className="tb-follow-row__avatar tb-follow-row__avatar--fallback"
                        aria-hidden="true"
                      >
                        {monogram(f.displayName)}
                      </span>
                    )}
                    <span className="tb-follow-row__name">
                      <Link to={`/users/${f.userId}`}>{f.displayName}</Link>
                    </span>
                    <button
                      type="button"
                      className="tb-btn tb-btn--small"
                      onClick={() => void unfollow(f.userId)}
                      disabled={busyId !== null}
                    >
                      {busyId === f.userId ? 'Unfollowing…' : 'Unfollow'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="tb-modal__foot">
          <div className="tb-modal__row">
            <button type="button" className="tb-btn" onClick={onClose} disabled={busyId !== null}>
              Done
            </button>
          </div>
        </div>
      </div>
      {/* </div>, NOT staging's </section>: this closes FollowingPanel, which is a MODAL on this branch
          (its root is `<div className="tb-modal__backdrop">` — see its return above). Staging's tag is a
          leftover from when the follow graph was an inline page section rather than a panel opened from
          the band's Following stat.

          The ~170 lines that followed staging's tag inside this same conflict are NOT incoming content:
          SupportRequestsSection and SupportRequestList already exist exactly once in this file, and they
          ARE those lines. Git pulled them into the hunk only because the one-line tag difference above
          knocked the two sides out of step; they are kept verbatim. (The block also carried a third
          function, DisplayedBadges, which really was dead on arrival — see the tombstone where it
          stood.) */}
    </div>
  );
}

/**
 * The RAISER's view of their own support requests (CH-F13 / FB-F15 raiser half, FB-F16, PR-F35).
 *
 * `GET /api/me/support-requests` has been built, scoped and proved on the server for a batch and read by
 * NOTHING: a person could raise a request and had no way to see what became of it, so the accountability
 * half of FB-F16 reached the database and no screen. This is the screen.
 *
 * PRIVATE, AND PRIVATE BY THE ROUTE, not by this component. The endpoint filters on
 * `raiser_user_id = requestUserId(req)` and there is deliberately no `/api/users/:id` counterpart — a
 * person's request for help is between them and the triagers. So there is nothing here to get wrong: no
 * id is sent, and an outsider asking gets their own (empty) list rather than somebody else's.
 *
 * ALL FOUR STATUSES INCLUDING CLOSED (PR-F35). A closed request is the outcome; hiding it would hide the
 * answer, which is precisely what the raiser came back for.
 *
 * THE HISTORY IS THE POINT. Each recorded move carries the triager's mandatory reason in their own words
 * (NT-F17) and NOT the triager's name — the raiser is owed the outcome and the reason, not a person to
 * take it up with. Kind 7's notification payload says exactly as much, so this list and the notification
 * feed cannot disagree about how much they tell.
 *
 * FETCHED HERE rather than in the /me loader: the loader is shared with the page's first paint and this
 * is a secondary block far down it, so a slow or failing triage read must not hold up (or fail) the
 * profile. A failure says so plainly instead of rendering an empty list, which would read as "nothing
 * has happened to your request" — the one thing this block exists to answer honestly.
 *
 * This component is the LOAD; `SupportRequestList` below is the RENDER, and the two are separate so the
 * markup can be exercised against a real response body without standing a browser up.
 */
function SupportRequestsSection() {
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'ok'; requests: MySupportRequest[] } | { kind: 'failed' }
  >({ kind: 'loading' });

  useEffect(() => {
    let live = true;
    void getMySupportRequests().then((result) => {
      if (!live) return;
      // 'unauthorized' lands here too: the page is only reachable signed in, so a 401 means the session
      // went away mid-visit. Reporting it as a failure is honest — we do not know the list is empty.
      setState(
        result.kind === 'ok' && result.body != null
          ? { kind: 'ok', requests: result.body.requests }
          : { kind: 'failed' },
      );
    });
    return () => {
      live = false;
    };
  }, []);

  return (
    <section aria-labelledby="requests-heading">
      <h2 id="requests-heading">Your support requests</h2>
      <div className="tb-private">
        <span className="tb-private__marker">Private — only you can see this</span>
        <p className="tb-profile__note" style={{ marginTop: 0 }}>
          Everything you&apos;ve asked for help with, and what has happened to it since.
        </p>
        {state.kind === 'loading' && <p className="tb-profile__note">Loading…</p>}
        {state.kind === 'failed' && (
          <p className="tb-profile__unset" style={{ margin: 0 }}>
            Your support requests couldn&apos;t be loaded just now. Please reload the page — this
            does not mean there are none.
          </p>
        )}
        {state.kind === 'ok' && <SupportRequestList requests={state.requests} />}
      </div>
    </section>
  );
}

/**
 * The raiser's requests, rendered. Split out from the load above so this markup can be driven straight
 * from a real `GET /api/me/support-requests` body.
 *
 * OWNER-ONLY. A support request is private to the raiser and the triagers, so nothing outside this page
 * should render one: the triage side has its own screen (`admin/SupportQueue.tsx`) which shows the
 * fields a triager is owed, not the fields a raiser is. Two audiences, two components, on purpose.
 */
export function SupportRequestList({ requests }: { requests: MySupportRequest[] }) {
  if (requests.length === 0) {
    return (
      <p className="tb-profile__unset" style={{ margin: 0 }}>
        You haven&apos;t asked for help yet.
      </p>
    );
  }
  return (
    <ul className="tb-request-list">
      {requests.map((r) => (
        <li key={r.requestId} className="tb-request">
          <div className="tb-request__head">
            <span className="tb-request__status">{r.status}</span>
            <span className="tb-request__meta">
              {r.origin === 'tool_report' ? 'Reported a problem' : 'Asked for help'}
              {/* A tool_report names its tool and links to it. An ask_for_help carries no tool at all —
                  the server forces tool_id NULL for that origin — so nothing is said about one, rather
                  than a "no tool" placeholder (UR-F04's rule, same instinct). */}
              {r.toolId !== null && r.toolName !== null && (
                <>
                  {' · '}
                  <Link to={`/tools/${r.toolId}`}>{r.toolName}</Link>
                </>
              )}
              {when(r.createdAt) !== null && ` · raised ${when(r.createdAt) ?? ''}`}
            </span>
          </div>
          <p className="tb-request__body">{r.body}</p>
          {/* Empty until a triager moves it, and said in words: an empty history and an unloaded one
              look identical, and only one of them means "nobody has looked at this yet". */}
          {r.history.length === 0 ? (
            <p className="tb-profile__note" style={{ margin: '0.4rem 0 0' }}>
              No update yet. You&apos;ll be notified when the status changes.
            </p>
          ) : (
            <ol className="tb-request__history">
              {r.history.map((h, i) => (
                <li key={`${h.changedAt}-${h.toStatus}-${i}`}>
                  {/* `fromStatus` is nullable in the schema for a first move from no status. Rendering
                      "null → Open" is the kind of leak UR-F04 is about, so that case gets its own words. */}
                  {h.fromStatus === null
                    ? `Set to ${h.toStatus}`
                    : `${h.fromStatus} → ${h.toStatus}`}
                  {when(h.changedAt) !== null && (
                    <span className="tb-request__meta"> · {when(h.changedAt)}</span>
                  )}
                  {anyFieldFilled([h.reason]) && (
                    <>
                      {' — '}
                      <span className="tb-request__reason">{h.reason}</span>
                    </>
                  )}
                </li>
              ))}
            </ol>
          )}
        </li>
      ))}
    </ul>
  );
}

/* REMOVED IN THE STAGING MERGE: `DisplayedBadges`, the owner's flat `.tb-profile__achievements` badge
 * strip. It reached this file only as an alignment passenger — the conflict that carried it was a
 * one-line closing-tag difference in SubmissionOverlay, and its render site was in a region the
 * redesign won, so it arrived declared and never called (tsc TS6133 caught it).
 *
 * `BadgeSlots` in the ProfileBand is the replacement and holds every requirement this had: the same
 * `!b.hidden` filter (PR-F43 / 0032), `criterionText` as each slot's tooltip (PR-F45), and no number,
 * total or comparison anywhere (PR-F58 / UR-F22 / PC-F31). The new-badge cue is still a dot whose label
 * says only "new badges to see" (PR-F46).
 *
 * ONE DELIBERATE DIFFERENCE: the band shows the first four non-hidden badges in fixed slots, where this
 * showed all of them. That is the Figma band's geometry, and the full set stays reachable — every slot
 * opens the Badge Panel. PR-NFR09/PR-F59's omission rule is unaffected: an empty slot renders a "+"
 * affordance, not a "no badges yet" prompt or a completion nudge. */

/**
 * WHAT SWITCHING A FIGURE OFF DOES NOT DO — said on the row, because the honest answer is not "nobody
 * can work it out any more".
 *
 * FIVE OF THE SIX are derivable from things this platform publishes anyway. Three are one click from
 * this very profile — it lists the person's shared tools (`PR-F29`), each tool page states its own
 * adoption count, and the `Safe Hands` badge says in as many words that the training is complete. The
 * other two are derivable by ENUMERATION rather than from any one page: every forum post and every tool
 * comment is publicly attributed to its author by name and id (`db/community.ts`, `db/toolFeedback.ts`
 * both return `authorUserId`/`authorName`), so a determined reader can count them. That is a weaker
 * disclosure than a stated total, and it is still a disclosure, so it is said rather than glossed.
 *
 * Turning a figure off removes the FIGURE — a stated number on a profile — and nothing else. It is a
 * display choice (`UR-F33`), not a confidentiality control, and letting a person believe otherwise by
 * saying nothing is the worse option.
 *
 * ONLY `badges_earned` HAS A REAL CONTROL BEHIND IT (the badge opt-out, which the server enforces by
 * withholding the count as well — see `readPublicContributionFiguresWith`), so it is the one figure with
 * no caveat here: for that one, switching off genuinely does withhold something.
 */
const CONTRIBUTION_FIGURE_CAVEAT: Record<string, string> = {
  tools_published:
    'Your shared tools are listed on your public profile either way, so this hides the number, not the list.',
  total_adoptions: 'Each of your tools shows its own adoption count on its own page, either way.',
  comments_made:
    'Your comments still show your name on the tools you commented on, so they can still be counted.',
  forum_posts: 'Your forum posts and replies still show your name, so they can still be counted.',
  learning_completed:
    'The Safe Hands badge says the same thing if you are showing it — hide the badge too if you would rather it were not visible.',
};

/**
 * The contribution-summary chooser (`UR-F21` / `PR-F56` / `UR-F33`): the CLOSED set of six figures,
 * with the person's choice of which appear on their public profile.
 *
 * WHY THIS SCREEN EXISTS. `app.contribution_display_choice` was created by migration 0012 and, until
 * this change, read and written by nothing at all — the summary `UR-F21` closes the set for had no
 * reader, no writer and no chooser, so "the summary may include…" was still being decided by nobody.
 *
 * ALL SIX ARE ALWAYS LISTED, each with its own current value, so the person can see what publishing a
 * figure would actually say BEFORE they publish it. That is owner-only data and the reason this reads
 * `GET /api/me/contribution-display` rather than deriving anything from the public profile: the public
 * payload deliberately carries no value for a figure that is switched off, so it could not answer this.
 *
 * THE DEFAULT IS THE REQUIREMENT'S, NOT THIS COMPONENT'S. Tools published, total adoptions and comments
 * made are on for a person who has never opened this (`CONTRIBUTION_SUMMARY_DEFAULT`), and the server
 * resolves that — nothing here knows which three they are, so the two cannot disagree.
 *
 * NO TOTAL, NO SCORE, NO COMPARISON (`UR-F22` / `PR-F58` / `PC-F31`). Six independent figures, in the
 * closed set's own order as the server returns them. Nothing sums them and nothing here says how the
 * person compares with anybody.
 *
 * FETCHED HERE rather than in the /me loader, like `SupportRequestsSection`: this is a secondary block
 * and a slow or failing read of it must not hold up or fail the profile's first paint.
 */
function ContributionSummarySection() {
  const [figures, setFigures] = useState<MyContributionFigure[] | null | 'loading'>('loading');
  const [busyFigure, setBusyFigure] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void fetchContributionDisplay().then((rows) => {
      if (live) setFigures(rows);
    });
    return () => {
      live = false;
    };
  }, []);

  // Send the DESIRED state (idempotent), then RE-READ rather than trusting the local flip: choosing
  // `badges_earned` can be accepted and still not publish it, and only the server knows that.
  async function toggle(figure: string, shown: boolean) {
    if (busyFigure !== null) return;
    setBusyFigure(figure);
    setError(null);
    const ok = await putContributionDisplay(figure, shown);
    if (ok) {
      setFigures(await fetchContributionDisplay());
    } else {
      setError('That change could not be saved. Please try again.');
    }
    setBusyFigure(null);
  }

  return (
    <section aria-labelledby="contribution-heading">
      <h2 id="contribution-heading">Your contribution summary</h2>
      <p className="tb-profile__note" style={{ marginTop: 0 }}>
        Six figures, and you choose which of them appear on your public profile. Nothing here is
        added up, scored or compared with anyone else.
      </p>
      {figures === 'loading' && <p className="tb-profile__note">Loading…</p>}
      {figures === null && (
        <p className="tb-profile__unset" style={{ margin: 0 }}>
          Your contribution summary couldn&apos;t be loaded just now. Please reload the page — this
          does not mean the figures are empty.
        </p>
      )}
      {Array.isArray(figures) && (
        <ul className="tb-figure-picker">
          {figures
            // A figure name this build does not know is SKIPPED, not rendered: the server owns the
            // closed set, and a column name is not a label to show a person.
            .filter((f) => CONTRIBUTION_FIGURE_LABEL[f.figure] !== undefined)
            .map((f) => (
              <li key={f.figure}>
                <label className={`tb-figure-row${f.shown ? ' tb-figure-row--on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={f.shown}
                    disabled={busyFigure !== null}
                    onChange={(e) => void toggle(f.figure, e.target.checked)}
                  />
                  <span>{CONTRIBUTION_FIGURE_LABEL[f.figure]}</span>
                  <span className="tb-figure-row__value">
                    {contributionFigureValueText(f.figure, f.value)}
                  </span>
                  {/* The server accepted the choice and is still withholding the figure. Said here,
                      in the row, rather than leaving a switch that appears to do nothing. */}
                  {f.withheldByBadgePrivacy && (
                    <p className="tb-profile__note tb-figure-row__note">
                      Not shown while any of your badges is hidden — the count would give away what
                      you hid. Show your badges again to publish this figure.
                    </p>
                  )}
                  {/* The same thing one figure along (PR-F30): the person hid a training step they
                      finished, and "Completed" means all seven — so publishing it would tell everyone
                      the hidden step was done. Named remedy, not a generic "withheld". */}
                  {f.withheldByLearningPrivacy && (
                    <p className="tb-profile__note tb-figure-row__note">
                      Not shown while you are hiding a training step you have completed — saying
                      your learning is complete would give that step away. Show that step below, or
                      leave this figure off.
                    </p>
                  )}
                  {!f.shown && CONTRIBUTION_FIGURE_CAVEAT[f.figure] !== undefined && (
                    <p className="tb-profile__note tb-figure-row__note">
                      {CONTRIBUTION_FIGURE_CAVEAT[f.figure]}
                    </p>
                  )}
                </label>
              </li>
            ))}
        </ul>
      )}
      {error && <p className="tb-modal__error">{error}</p>}
    </section>
  );
}

/**
 * The chosen-learning chooser (`PR-F30` / `UR-F33`): the seven steps of the mandatory training, with the
 * person's choice of which of the ones they have COMPLETED appear on their public profile.
 *
 * WHY THIS SCREEN EXISTS. `PR-F30` was the sole blocker on `PR-F02`: the profile had no way to publish
 * completed learning and — more to the point — no way for a person to decide whether any of it was
 * published. Storage (0047), the readers and this chooser landed together, because a chooser without a
 * public read publishes nothing and a public read without a chooser publishes without asking.
 *
 * NOTHING IS SHOWN UNTIL IT IS CHOSEN, and this is the opposite of the contribution summary above. Every
 * step starts hidden (`PR-F30`: "nothing learning-related is shown publicly unless they have chosen to
 * show it"), so a person who never opens this section publishes no learning at all — which is why the
 * copy says so out loud rather than leaving them to infer it from seven unticked boxes. The server owns
 * that default; nothing here knows it, so the two cannot disagree.
 *
 * ALL SEVEN ARE ALWAYS LISTED, INCLUDING THE ONES THEY HAVE NOT DONE, each marked as such. A chooser that
 * hid the steps you had not finished would answer "why is this not on my profile?" with silence; and the
 * choice is genuinely stored for an unfinished step, taking effect if and when they complete it. What the
 * row must not do is imply the step is published: `shown` on an incomplete step publishes nothing, the
 * server joins through the completion record, and the row says exactly that.
 *
 * NO PROGRESS FIGURE HERE (`PC-F31`). This is a list of choices, not a "4 of 7" score — the Learning Hub
 * is where a person sees their own progress. Nothing on this screen counts, totals or compares.
 *
 * FETCHED HERE rather than in the /me loader, like `ContributionSummarySection` and
 * `SupportRequestsSection`: a secondary block, and a slow or failing read of it must not hold up or fail
 * the profile's first paint.
 */
function ChosenLearningSection() {
  const [steps, setSteps] = useState<MyLearningStep[] | null | 'loading'>('loading');
  const [busyStep, setBusyStep] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void fetchLearningDisplay().then((rows) => {
      if (live) setSteps(rows);
    });
    return () => {
      live = false;
    };
  }, []);

  // Send the DESIRED state (idempotent), then RE-READ rather than trusting the local flip — the same
  // shape the contribution chooser uses, and for a related reason: hiding a completed step also withholds
  // the `learning_completed` contribution figure, and only the server knows that.
  async function toggle(stepNo: number, shown: boolean) {
    if (busyStep !== null) return;
    setBusyStep(stepNo);
    setError(null);
    const ok = await putLearningDisplay(stepNo, shown);
    if (ok) {
      setSteps(await fetchLearningDisplay());
    } else {
      setError('That change could not be saved. Please try again.');
    }
    setBusyStep(null);
  }

  return (
    <section aria-labelledby="chosen-learning-heading">
      <h2 id="chosen-learning-heading">Your learning on your profile</h2>
      <p className="tb-profile__note" style={{ marginTop: 0 }}>
        None of your learning appears on your public profile unless you choose to show it here. Only
        steps you have completed can be shown, and nothing here is scored or compared with anyone.
      </p>
      {steps === 'loading' && <p className="tb-profile__note">Loading…</p>}
      {steps === null && (
        <p className="tb-profile__unset" style={{ margin: 0 }}>
          Your learning choices couldn&apos;t be loaded just now. Please reload the page — this does
          not mean anything has been published.
        </p>
      )}
      {Array.isArray(steps) && (
        <ul className="tb-figure-picker">
          {steps
            // A step number this build cannot name is SKIPPED, not rendered: the closed set is the
            // server's, and "Step 8" is not a label to offer somebody.
            .filter((s) => LEARNING_STEP_LABEL[s.stepNo] !== undefined)
            .map((s) => (
              <li key={s.stepNo}>
                <label className={`tb-figure-row${s.shown ? ' tb-figure-row--on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={s.shown}
                    disabled={busyStep !== null}
                    onChange={(e) => void toggle(s.stepNo, e.target.checked)}
                  />
                  <span>{LEARNING_STEP_LABEL[s.stepNo]}</span>
                  <span className="tb-figure-row__value">
                    {s.completed ? 'Completed' : 'Not completed yet'}
                  </span>
                  {/* The choice is stored either way — it is a standing permission — so the row says
                      what it will and will not do rather than pretending the tick did nothing. */}
                  {s.shown && !s.completed && (
                    <p className="tb-profile__note tb-figure-row__note">
                      Nothing is shown for this step until you complete it.
                    </p>
                  )}
                </label>
              </li>
            ))}
        </ul>
      )}
      {error && <p className="tb-modal__error">{error}</p>}
    </section>
  );
}

/**
 * The Badge Panel (PR-F44) — the owner's FULL badge panel. It shows EVERY badge in the closed
 * masterlist (PR §5.2) in two states, following Will's Badge Panel flow:
 *   - EARNED     → rendered normally as a per-badge switch (role=switch) that is ON (Shown) by
 *     DEFAULT. Switching it off HIDES that badge from other people (PR-F43, 0032). Each change calls
 *     PUT /api/me/badges/:key/hidden with the desired state (the server enforces earned-only), then
 *     re-reads the panel and revalidates the /me loader so the strip and switches reflect the save.
 *   - NOT EARNED → rendered visibly MUTED and non-interactive (Will's "not earned → how-to-achieve"
 *     node), with the masterlist criterion wording shown as the how-to-earn text (PR-F45), inline and
 *     on hover/title. A person should never have to guess how a badge is earned.
 *
 * OWNER-ONLY and PRIVATE: opened only from the owner's own /me. The list is fetched lazily on open
 * from GET /api/me/badges/panel (owner-scoped) — the unearned half is owner-private (PR-F05), never a
 * public projection. The /me loader is NOT changed; the profile-load path stays as light as before.
 *
 * NO CAP: every earned badge is public by default; the owner may hide any number of them. There is no
 * ceiling and no "you can show up to N" gate — the only choice is per-badge shown/hidden.
 *
 * ANTI-RANKING (PR-F58 / UR-F22 / PC-F31): no number, total, score or comparison — just the list with
 * a shown/hidden switch on earned rows and the criterion wording on every row.
 */
function BadgePanel({
  onClose,
  returnFocusRef,
  newKeys,
  badgesHidden,
}: {
  onClose: () => void;
  returnFocusRef: React.RefObject<HTMLButtonElement>;
  /**
   * The earned badge keys the viewer had not yet seen when they opened the panel (PR-F46). Each is
   * given a subtle "new" tag on its row so the person can spot what just arrived — the discovery is
   * on THIS screen, which they chose to open (not a notification). Cleared for next time the moment
   * the panel opened (the parent marked them seen). Empty is the normal case.
   */
  newKeys: string[];
  /** The owner's current public-visibility opt-out (0030). Drives the "Don't display…" checkbox. */
  badgesHidden: boolean;
}) {
  const newKeySet = new Set(newKeys);
  const revalidator = useRevalidator();
  const [panel, setPanel] = useState<PanelBadge[] | null>(null); // null = still loading
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Local mirror of the opt-out so the checkbox reflects the tick instantly; the loader revalidation
  // makes it authoritative. Seeded from the loader value the panel was opened with.
  const [hidden, setHidden] = useState(badgesHidden);
  const [privacyBusy, setPrivacyBusy] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Toggle the public-visibility opt-out (0030). Optimistic: flip the local mirror, PUT, and on
  // failure roll back. Revalidate so the owner's own strip and /api/me re-read the saved flag.
  async function togglePublic(nextHidden: boolean) {
    if (privacyBusy) return;
    setPrivacyBusy(true);
    setError(null);
    setHidden(nextHidden);
    const result = await putBadgesPublic(nextHidden);
    if (result.ok) {
      revalidator.revalidate();
    } else {
      setHidden(!nextHidden); // roll back the optimistic flip
      setError('Something went wrong. Please try again.');
    }
    setPrivacyBusy(false);
  }

  // Fetch the full masterlist when the panel opens.
  useEffect(() => {
    let alive = true;
    void fetchBadgePanel().then((rows) => {
      if (alive) setPanel(rows);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Move focus into the panel on open; return it to the trigger on close (unmount).
  useEffect(() => {
    headingRef.current?.focus();
    const trigger = returnFocusRef.current;
    return () => trigger?.focus();
  }, [returnFocusRef]);

  // Set a badge's desired shown/hidden state (idempotent — we send what the switch will show). The
  // switch reads as ON = Shown, so switching a Shown badge off means "hide it" (putBadgeHidden true).
  async function toggle(badgeKey: string, nextHidden: boolean) {
    if (busyKey) return;
    setBusyKey(badgeKey);
    setError(null);
    const result = await putBadgeHidden(badgeKey, nextHidden);
    if (result.ok) {
      revalidator.revalidate(); // re-run meLoader → the on-page strip re-reads the saved shown set
      // Re-read the panel so the open dialog reflects the new hidden state immediately.
      const rows = await fetchBadgePanel();
      setPanel(rows);
      setBusyKey(null);
      return;
    }
    setBusyKey(null);
    setError('Something went wrong. Please try again.');
  }

  // One badge row. Earned → a shown/hidden switch (role=switch, ON = Shown by default); not-yet-earned
  // → a muted, non-interactive row with its how-to-earn criterion (PR-F45). No number/score anywhere.
  function renderRow(b: PanelBadge) {
    if (!b.earned) {
      return (
        <li key={b.badgeKey}>
          <div
            className="tb-badge-picker__row tb-badge-picker__row--locked"
            aria-disabled="true"
            title={b.criterionText}
          >
            <BadgeIcon badgeKey={b.badgeKey} earned={false} className="tb-badge-picker__icon" />
            <span className="tb-badge-picker__name">{b.name}</span>
            <span className="tb-badge-picker__criterion">{b.criterionText}</span>
            <span className="tb-badge-picker__state" aria-hidden="true">
              Locked
            </span>
          </div>
        </li>
      );
    }
    // Shown is the default (aria-checked = NOT hidden). Clicking flips to the opposite state.
    const shown = !b.hidden;
    return (
      <li key={b.badgeKey}>
        <button
          type="button"
          className="tb-badge-picker__row"
          role="switch"
          aria-checked={shown}
          aria-label={`Show ${b.name} on your profile`}
          title={b.criterionText}
          onClick={() => void toggle(b.badgeKey, shown)}
          disabled={busyKey !== null}
        >
          <BadgeIcon badgeKey={b.badgeKey} className="tb-badge-picker__icon" />
          <span className="tb-badge-picker__name">
            {b.name}
            {/* A subtle "new" tag on a badge the person had not yet looked at (PR-F46 discovery on
                this screen — not a notification, not a count). Only earned rows can be new. */}
            {newKeySet.has(b.badgeKey) && <span className="tb-badge-picker__new">new</span>}
          </span>
          <span className="tb-badge-picker__criterion">{b.criterionText}</span>
          <span className="tb-badge-picker__state" aria-hidden="true">
            {shown ? 'Shown' : 'Hidden'}
          </span>
        </button>
      </li>
    );
  }

  // Group the flat masterlist by its §5.2 section, preserving encounter order (= sort_order), so the
  // panel reads as the requirements' own sections rather than one long list. This is a LAYOUT of the
  // closed list, not a ranking or a count (PR-F58 / UR-F22 / PC-F31): no group shows a total.
  const groups: { name: string; badges: PanelBadge[] }[] = [];
  if (panel) {
    for (const b of panel) {
      let g = groups.find((x) => x.name === b.group);
      if (!g) {
        g = { name: b.group, badges: [] };
        groups.push(g);
      }
      g.badges.push(b);
    }
  }

  return (
    <div
      className="tb-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        className="tb-modal tb-modal--tall"
        role="dialog"
        aria-modal="true"
        aria-labelledby="badge-panel-heading"
      >
        <h2 id="badge-panel-heading" ref={headingRef} tabIndex={-1}>
          Your badges
        </h2>
        <p className="tb-profile__note tb-modal__intro">
          Every badge, and how it is earned. All the badges you’ve earned appear on your profile by
          default — switch one off to hide it from other people. Only you can see this panel.
        </p>
        <div className="tb-modal__scroll">
          {panel === null ? (
            <p className="tb-profile__note">Loading…</p>
          ) : (
            <div className="tb-badge-picker">
              {groups.map((g) => (
                <section key={g.name} className="tb-badge-group" aria-label={`${g.name} badges`}>
                  <p className="tb-badge-group__head">
                    <span className="tb-badge-group__title">{g.name}</span>
                    {BADGE_GROUP_GLOSS[g.name] && (
                      <span className="tb-badge-group__gloss"> — {BADGE_GROUP_GLOSS[g.name]}</span>
                    )}
                  </p>
                  <ul>{g.badges.map(renderRow)}</ul>
                </section>
              ))}
            </div>
          )}
        </div>
        <div className="tb-modal__foot">
          {/* GLOBAL public-visibility opt-out (0030, PR-F43) — the coarse control layered on top of
              the per-badge switches. Ticked = ALL your badges are hidden from OTHER people, whatever
              their individual switches say; you always see them here and on your own profile. */}
          <label className="tb-badge-privacy">
            <input
              type="checkbox"
              checked={hidden}
              onChange={(e) => void togglePublic(e.target.checked)}
              disabled={privacyBusy}
            />
            <span>
              Don&rsquo;t display my badges publicly
              <span className="tb-profile__note tb-badge-privacy__hint">
                {hidden
                  ? 'All your badges are hidden from other people. Only you can see them.'
                  : 'Your badges are visible on your public profile unless you switch them off.'}
              </span>
            </span>
          </label>
          <div className="tb-modal__row">
            <button type="button" className="tb-btn" onClick={onClose} disabled={busyKey !== null}>
              Done
            </button>
          </div>
          {error && <p className="tb-modal__error">{error}</p>}
        </div>
      </div>
    </div>
  );
}

/** The signed-out state: a plain, honest prompt with a link to the backend login route. */
function SignedOut() {
  return (
    <section aria-labelledby="signin-heading" className="tb-profile">
      <style>{PROFILE_STYLES}</style>
      <h1 id="signin-heading">Your profile</h1>
      <p style={{ maxWidth: '52ch' }}>
        Sign in with your work account to see your profile. Trailblazer reads only your name and
        directory details from Microsoft — it never sees your password.
      </p>
      {/*
        A plain link (full-page navigation) to the BACKEND login route. The backend — not this
        page — holds the client secret and drives the Microsoft round-trip; the browser only
        follows redirects. After sign-in the backend returns us to /me.
      */}
      <a className="tb-signin-btn" href="/auth/login?returnTo=/me">
        Sign in with your work account
      </a>
    </section>
  );
}

/**
 * BadgeSlots — the four 64px badge squares in the navy band (Figma 20:1720). Badges carry no image
 * field yet (MyBadge = name/criterion only), so a filled slot shows the badge NAME as a text tile;
 * empty slots show a "+" add affordance. There is no separate "Choose badges" button — EVERY square
 * is a button that opens the full BadgePanel (the only place to manage which badges show). One slot
 * carries `badgeEditRef` so BadgePanel's return-focus contract still lands somewhere sensible.
 *
 * The quiet "new badges to see" cue (PR-F46) rides as a dot on the first slot — a plain visual hint,
 * never a count (PR-F58 / UR-F22 / PC-F31). Opening the panel clears it (handled by `onOpen`, which
 * is the parent's `openBadgePanel`, marking earned badges seen).
 */
function BadgeSlots({
  badges,
  onOpen,
  triggerRef,
  hasNewBadges,
}: {
  badges: MyBadge[];
  onOpen: () => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
  hasNewBadges: boolean;
}) {
  const shown = badges.filter((b) => !b.hidden);
  const SLOT_COUNT = 4;
  const slots = Array.from({ length: SLOT_COUNT }, (_, i) => shown[i] ?? null);

  return (
    <div className="pp-badges">
      <span className="pp-badges__label">Badges</span>
      <div className="pp-badge-grid">
        {slots.map((badge, i) => {
          const isFirst = i === 0;
          return (
            <button
              key={badge ? badge.badgeKey : `add-${i}`}
              type="button"
              ref={isFirst ? triggerRef : undefined}
              className={`pp-badge-slot${badge ? '' : ' pp-badge-slot--add'}`}
              aria-haspopup="dialog"
              aria-label={badge ? `${badge.name} — choose badges` : 'Add a badge — choose badges'}
              title={badge ? badge.criterionText : undefined}
              onClick={onOpen}
            >
              {badge ? (
                /* THE MEDALLION, WITH ITS NAME ON IT. `label` puts the title inside the square rather
                   than beside it, so a tile is self-describing — twenty medallions are hard to tell
                   apart by glyph alone. The tile's tier colour is what reads at this size; the title is
                   readable from about 120px up, and this tile is a measured 64px Figma square, so here
                   it is a shape rather than a word. The name is also still this button's aria-label
                   (above) and the criterion is still its tooltip, so nothing depends on reading it. */
                <BadgeIcon badgeKey={badge.badgeKey} label={badge.name} />
              ) : (
                <span className="pp-badge-slot__plus" aria-hidden="true">
                  +
                </span>
              )}
              {/* PR-F46: a dot when there are earned badges this viewer has not yet looked at. Not a
                  count — the accessible label says only "new badges to see". */}
              {isFirst && hasNewBadges && (
                <span className="pp-badge-slot__newdot" role="img" aria-label="new badges to see" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * ProfileBand — the full-bleed navy identity band (Figma 20:1720): 128px avatar (with the edit-pencil
 * affordance), the identity cluster (name · role, follower/following stats, bio), and the badge
 * squares on the right. Renders the person's REAL data; the avatar and badge picker reuse the existing
 * AvatarPanel / BadgePanel flows via the passed-in handlers and focus refs.
 */
function ProfileBand({
  displayName,
  avatar,
  role,
  bio,
  followerCount,
  followingCount,
  badges,
  hasNewBadges,
  onEditAvatar,
  avatarEditRef,
  onOpenBadges,
  badgeTriggerRef,
  onOpenFollowing,
  followingTriggerRef,
}: {
  displayName: string;
  avatar: string | null;
  role: string | null;
  bio: string | null;
  followerCount: number;
  followingCount: number;
  badges: MyBadge[];
  hasNewBadges: boolean;
  onEditAvatar: () => void;
  avatarEditRef: React.RefObject<HTMLButtonElement>;
  onOpenBadges: () => void;
  badgeTriggerRef: React.RefObject<HTMLButtonElement>;
  onOpenFollowing: () => void;
  followingTriggerRef: React.RefObject<HTMLButtonElement>;
}) {
  return (
    <div className="pp-band-bleed">
      <div className="pp-band">
        {/* Decorative lime contour backdrop spanning the whole band. Ornamental only: aria-hidden and
            pointer-events:none, so it is absent from the accessibility tree and never intercepts a click,
            and it is the band's FIRST child so every real element paints over it. Nothing is lost if
            WebGL is unavailable and it renders nothing; it draws a single static frame under reduced
            motion.

            The tuning below is all about WHERE the eye lands, so each value is deliberate:

            fadeCenter [0.62, 0.5] puts the brightest point off-centre — past the end of the name/bio
              block but before the badge column, i.e. in the layout's real gap. y is 0.5 because 0,0 is
              the BOTTOM-left in GL coordinates, so this is vertically centred.
            fadeInner 0.0 / fadeOuter 1.5 make the falloff one continuous ramp from that hot point all the
              way past the box edge, so the field flows out to the edges instead of ending in a visible
              ring. Values >1 are intentional: the fade is normalised so 1.0 IS the edge, so 1.5 means it
              is still mid-ramp when it reaches the corners.
            opacity 0.55 and glow 0.35 are well above the component's conservative defaults — the brief
              was vibrancy, and the hot point can carry it because the mask still clears both ends.
            aspectCorrect 1 keeps the contours round and lets the short band CROP them, rather than
              squashing the rings vertically to fit (the default, but stated here because it is the whole
              reason the lines read as drawn rather than stretched).
            fadeAspect 1.9 widens the fade ellipse horizontally so the wide band does not fade out
              vertically long before it does horizontally. */}
        {/* `null` fallback, deliberately: the backdrop is decorative and absolutely positioned, so
            rendering nothing while its chunk arrives is invisible — a spinner or a placeholder box here
            would be more disruptive than the brief absence of the contours. */}
        <Suspense fallback={null}>
          <Topography
            className="pp-band__topo"
            fadeCenter={[0.62, 0.5]}
            fadeInner={0}
            fadeOuter={1.5}
            fadeAspect={1.9}
            aspectCorrect={1}
            opacity={0.55}
            glow={0.35}
          />
        </Suspense>
        <div className="pp-avatar-wrap">
          {avatar ? (
            // eslint-disable-next-line jsx-a11y/img-redundant-alt -- alt is a name, intentional
            <img className="pp-avatar" src={avatar} alt={`${displayName}'s profile photo`} />
          ) : (
            <div className="pp-avatar pp-avatar--fallback" aria-hidden="true">
              {monogram(displayName)}
            </div>
          )}
          <button
            type="button"
            ref={avatarEditRef}
            className="pp-avatar-edit"
            aria-label="Edit your profile"
            aria-haspopup="dialog"
            onClick={onEditAvatar}
          >
            {/* Pencil-edit glyph (Downloads/Vector.svg), stripped of the Figma frame artifacts to just
                the underline bar + pencil paths; currentColor so it inherits the button's ink. */}
            <svg
              className="pp-avatar-edit__icon"
              viewBox="0 0 14 13"
              width="16"
              height="16"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M14 12H0V13H14V12Z" fill="currentColor" />
              <path
                d="M11.7 3.5C12.1 3.1 12.1 2.5 11.7 2.1L9.9 0.3C9.5 -0.1 8.9 -0.1 8.5 0.3L1 7.8V11H4.2L11.7 3.5ZM9.2 1L11 2.8L9.5 4.3L7.7 2.5L9.2 1ZM2 10V8.2L7 3.2L8.8 5L3.8 10H2Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>

        <div className="pp-cluster">
          <div className="pp-name-row">
            <h1 id="profile-heading" className="pp-name">
              {displayName}
            </h1>
            {role && <span className="pp-role">{role}</span>}
          </div>
          <div className="pp-stats">
            {/* PR-F52: the owner's own follower COUNT — a bare number, never who follows them. This
                stays plain text: there is no "who follows me" list to open. */}
            <span className="pp-stat">
              <b>{followerCount}</b> {followerCount === 1 ? 'Follower' : 'Followers'}
            </span>
            {/* Following IS a list the owner can open (their own "who I follow", with unfollow), so the
                count is a button that opens the FollowingPanel. */}
            <button
              type="button"
              ref={followingTriggerRef}
              className="pp-stat pp-stat--button"
              aria-haspopup="dialog"
              onClick={onOpenFollowing}
            >
              <b>{followingCount}</b> Following
            </button>
          </div>
          {bio && <p className="pp-bio">{bio}</p>}

          {/* OWNER-ONLY UTILITY LINK: the way in to Settings.
              Ported here from the legacy `.tb-profile__head` block that this band replaced (the staging
              merge) — that head rendered on /me alone, and so does this band, so the ownership property
              that made them safe is unchanged.

              BOTH links, deliberately, and that reasoning is staging's: the tour is something you do to
              your own account, so one hop is the right cost; and Settings stays because the tour's own
              closing card tells people they can reopen it "from Settings on your profile", which
              dropping the link would make a lie. Neither touches PRIMARY_NAV, which PC-F10 pins at
              exactly seven entries.

              STYLED, not carried across verbatim: the old head sat on the page's light surface, so a
              bare <Link> inherited a readable default. This band is dark navy (#cddcdc/#dfeceb ink), and
              an unstyled link there renders in the browser's default blue on navy — a real contrast
              failure against PC-NFR01. `.pp-band-links` puts them on the band's own light ink. */}
          <p className="pp-band-links">
            <Link className="pp-band-link" to="/settings">
              Settings
            </Link>
          </p>
        </div>

        <BadgeSlots
          badges={badges}
          onOpen={onOpenBadges}
          triggerRef={badgeTriggerRef}
          hasNewBadges={hasNewBadges}
        />
      </div>
    </div>
  );
}

export function Profile() {
  const data = useLoaderData() as MeLoaderData;

  // The signed-out branch renders NO hooks, so the hooks the signed-in view needs cannot live in
  // this function after the early return (they would be called conditionally — rules-of-hooks, and
  // a real "rendered fewer hooks than expected" crash risk when the branch flips). The signed-in
  // body — which reads `me`/`badges` that only exist when signed in — is its own component, so its
  // hooks are unconditional. Profile() is now just the gate.
  if (!data.signedIn) return <SignedOut />;
  return <ProfileSignedIn data={data} />;
}

/** The signed-in profile. Rendered only when `data.signedIn` is true, so `me`/`badges` are present
 *  and every hook below runs unconditionally (rules-of-hooks). */
function ProfileSignedIn({ data }: { data: Extract<MeLoaderData, { signedIn: true }> }) {
  const { me, shared, adopted, saved, badges, followerCount, following } = data;
  const p = me.profile;
  const self = me.selfDeclared;
  const directoryPhoto = p?.photo_blob_ref ?? null;
  // Presentation-only override (UR-F30): a selected stock/uploaded avatar wins over the directory photo.
  const avatar = resolveAvatar(self, directoryPhoto);
  const hasReplacement =
    (self?.avatarKind === 'stock' || self?.avatarKind === 'upload') && !!self.avatarBlobRef;
  // TAKEN FROM STAGING, and required rather than preferred: the Identity section further down this
  // function already renders `memberSince`, `lastSeen` and `synced`, and gates itself on `hasIdentity`.
  // Our side of this conflict was empty, so dropping these would have left that markup referencing four
  // undeclared names. `when` and `anyFieldFilled` come from the shared ./people/fields helper (see the
  // import note at the top of this file).
  const synced = when(p?.graph_synced_at ?? null);
  const memberSince = when(me.platform.member_since);
  const lastSeen = when(me.platform.last_seen);
  // UR-F04 carried up to the section: with every row now hiding itself when empty, the Identity heading
  // must go too when nothing fills it. `me.upn` is always present in practice (it is the session
  // identity), so this is a guard rather than a live branch — but it is the same predicate the rows use,
  // so the two cannot fall out of step if a future shape makes a field optional.
  const hasIdentity = anyFieldFilled([
    me.upn,
    me.email,
    p?.job_title,
    p?.department,
    p?.office_location,
    p?.manager_name,
    memberSince,
    lastSeen,
  ]);

  const [avatarPanelOpen, setAvatarPanelOpen] = useState(false);
  const avatarEditRef = useRef<HTMLButtonElement>(null);
  const [badgePanelOpen, setBadgePanelOpen] = useState(false);
  const badgeEditRef = useRef<HTMLButtonElement>(null);
  const [followingPanelOpen, setFollowingPanelOpen] = useState(false);
  const followingTriggerRef = useRef<HTMLButtonElement>(null);
  // Publish NAVIGATES to the Kombi share journey at `/share`. It used to open the legacy upload wizard
  // (ShareOverlay) in place over this page; the journey that replaced it is a full page with its own
  // header and four URLs, so it cannot be hosted in this page's modal shell. The overlay is unchanged
  // and still reachable at `/share/legacy`.
  const publishTriggerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  // The quiet "new badge" affordance (PR-F46 / NT §8 — NOT a notification). Badges are awarded
  // silently; the person discovers them "when they next look at their own profile". This helps them
  // notice there IS something to look at — a per-viewer, client-side thing only (§5.1 rule 1: no new
  // server tracking). `newKeys` are the earned badges this viewer has not yet SEEN in the panel.
  // Opening the panel is the act of looking, so it clears them. No count is derived from this — only
  // a boolean (is anything new?) and a per-key flag (PR-F58 / UR-F22 / PC-F31: never a total/score).
  const earnedKeys = badges.map((b) => b.badgeKey);
  const [newKeys, setNewKeys] = useState<string[]>(() => newBadgeKeys(me.user_id, earnedKeys));
  const hasNewBadges = newKeys.length > 0;

  // Recompute when the earned set changes (a fresh award arrived on a revalidated load). Depends on a
  // stable string of the keys so it re-runs only when the actual set changes, not on every render.
  const earnedKeysSig = earnedKeys.join(',');
  useEffect(() => {
    setNewKeys(newBadgeKeys(me.user_id, earnedKeys));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- earnedKeysSig captures earnedKeys
  }, [me.user_id, earnedKeysSig]);

  function openBadgePanel() {
    // Looking IS discovery (PR-F46): mark every earned badge seen so the dot + row tags clear, and
    // reflect that in this render immediately (no wait for a reload).
    markBadgesSeen(me.user_id, earnedKeys);
    setNewKeys([]);
    setBadgePanelOpen(true);
  }

  // The band's role line (Figma: "Plumber"). Directory-sourced job title and department, joined when
  // both are present; falls back to whichever exists; null (line omitted) when neither does.
  const role = [p?.job_title, p?.department].filter(Boolean).join(' · ') || null;

  return (
    <section aria-labelledby="profile-heading" className="tb-profile pp-page">
      <style>{PROFILE_STYLES}</style>

      {/* Full-bleed navy identity band (Figma 20:1720): avatar + identity cluster + badge squares.
          Badges live INSIDE the band, top-right; every square opens the BadgePanel (no separate
          "Choose badges" button). The band replaces the legacy small head + separate badges strip. */}
      <ProfileBand
        displayName={me.display_name}
        avatar={avatar}
        role={role}
        bio={self?.bio ?? null}
        followerCount={followerCount}
        followingCount={following.length}
        badges={badges}
        hasNewBadges={hasNewBadges}
        onEditAvatar={() => setAvatarPanelOpen(true)}
        avatarEditRef={avatarEditRef}
        onOpenBadges={openBadgePanel}
        badgeTriggerRef={badgeEditRef}
        onOpenFollowing={() => setFollowingPanelOpen(true)}
        followingTriggerRef={followingTriggerRef}
      />

      {avatarPanelOpen && (
        <AvatarPanel
          self={self}
          certifications={me.certifications}
          hasReplacement={hasReplacement}
          onClose={() => setAvatarPanelOpen(false)}
          returnFocusRef={avatarEditRef}
        />
      )}

      {badgePanelOpen && (
        <BadgePanel
          onClose={() => setBadgePanelOpen(false)}
          returnFocusRef={badgeEditRef}
          newKeys={newKeys}
          badgesHidden={self?.badgesHidden ?? false}
        />
      )}

      {/* BOTH SIDES OF THIS CONFLICT ARE KEPT — they are four unrelated sections that git aligned only
          because they share this slot, not two designs for one thing. "Your Uploads" (the redesign) is
          first because it is the page's own primary action; the three that follow are staging's and each
          is load-bearing: ContributionSummarySection and ChosenLearningSection are declared in this very
          file and rendered NOWHERE else, so taking only our side would have left both features defined
          and dead, and Identity is the consumer of the `memberSince`/`lastSeen`/`synced`/`hasIdentity`
          locals kept earlier in this function. */}
      {/* "Your Uploads" (Figma Frame 66): heading + Publish, above the bordered container that holds
          the shared-tools gallery. Publish opens the "Share a tool" wizard (ShareOverlay) IN PLACE over
          this page — the same flow /share hosts, but with no navigation, so the page stays instant and
          any data load is a brief spinner inside the just-opened overlay. This is Will's ProfileFlow
          entry point: each gallery panel opens the overlay. */}
      <section className="pp-section" aria-labelledby="shared-heading">
        <div className="pp-section-head">
          <h2 id="shared-heading">Your Uploads</h2>
          {/* Publish now NAVIGATES to the share journey rather than opening the legacy overlay in
              place. The journey is a full Kombi page with its own header and four URLs, so it cannot be
              hosted inside this page's modal shell — and hosting it would pull the legacy Tailwind
              bundle onto a Kombi surface. `/share` is the journey's first step. The overlay itself is
              unchanged and still reachable at `/share/legacy`. */}
          <Button
            ref={publishTriggerRef}
            variant="solid-square"
            size="sm"
            className="pp-publish-btn"
            onClick={() => navigate('/share')}
          >
            Publish
          </Button>
        </div>
        <div className="pp-uploads-box">
          <SubmissionsSection tools={shared} />
        </div>
      </section>

      {/* The contribution summary chooser (UR-F21/UR-F33/PR-F56), directly after the badges it sits
          beside on the public profile — both answer "what do other people see about what I've done?",
          and keeping them apart is how a person ends up thinking they have set one when they set the
          other. Fetches itself (see the component), so it cannot delay the first paint. */}
      <ContributionSummarySection />

      {/* The chosen-learning chooser (PR-F30/UR-F33), immediately BESIDE the contribution one and after
          it on purpose: "Learning completed" is one of those six figures, and hiding a completed step
          withholds that figure — so the row that explains why points down at this section. Same reason
          they are adjacent as the badges are adjacent to both. Fetches itself, so it cannot delay the
          first paint. */}
      <ChosenLearningSection />

      {/* Identity — directory-sourced (public fields) plus the person's own platform record. Every row
          hides itself when the directory did not supply it (UR-F04), so the section is gated on there
          being a row to show: the sign-in name always is in practice, but a heading over an empty list
          is the hole the requirement closes and the guard costs nothing. The per-field `&&` tests the
          rows below used to carry are gone — `Field` owns that decision now, and a caller repeating it
          is how the two rules start disagreeing. */}
      {hasIdentity && (
        <section aria-labelledby="identity-heading">
          <h2 id="identity-heading">Identity</h2>
          <dl>
            <Field label="Sign-in name" value={me.upn} />
            <Field label="Email" value={me.email} />
            <Field label="Job title" value={p?.job_title ?? null} />
            <Field label="Department" value={p?.department ?? null} />
            <Field label="Office" value={p?.office_location ?? null} />
            <Field label="Manager" value={p?.manager_name ?? null} />
            <Field label="Member since" value={memberSince} />
            <Field label="Last seen" value={lastSeen} />
          </dl>
          {synced && (
            <p className="tb-profile__synced">
              Directory details last synced from Microsoft on {synced}.
            </p>
          )}
        </section>
      )}

      {/* "My Tool Collection": the owner's saved + adopted tools under one header, with a Saved/Adopted
          toggle switching a bento grid of tool tiles. Replaces the old two-column click-to-reveal
          "Saved by me" / "Adopted by me" boxes — same owner-private adopted/saved data inside. */}
      <MyToolCollection saved={saved} adopted={adopted} />

      {/* Follow graph (PR-F52/F61): the follower COUNT is a bare number in the band; the private
          "who I follow" list opens as a modal from the band's "Following" stat button. There is no
          "who follows me" list — that is shown to nobody. */}
      {followingPanelOpen && (
        <FollowingPanel
          following={following}
          onClose={() => setFollowingPanelOpen(false)}
          returnFocusRef={followingTriggerRef}
        />
      )}

      {/* MOSTLY OURS, WITH ONE RESCUE. Of staging's four sections in this slot, three are markup for
          components that no longer exist and would not compile:
            · the two <PrivateToolList> lists — replaced by <MyToolCollection>, which shows the same
              adopted/saved data under one header with a toggle (rendered above).
            · <FollowingSection> — replaced by the band's Follower/Following stats, where the Following
              count is a button opening <FollowingPanel> (mounted just below).
          `PrivateToolList` and `FollowingSection` have ZERO declarations left in this file; only the
          call sites survived the merge, which is why this is a compile fact and not a preference.

          <SupportRequestsSection /> IS KEPT, at the end: it is declared in this file and this was its
          only render site, so dropping it with the rest would have silently removed CH-F13/PR-F35 — the
          raiser's own view of what happened to their support requests. */}

      {/* CH-F13/PR-F35: the raiser's side of support. Fetches itself (see the component) rather than
          riding the /me loader, so a slow triage read cannot hold up the profile's first paint. */}
      <SupportRequestsSection />
    </section>
  );
}
